import React, {useState, useEffect, useRef} from 'react'
import {DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, pointerWithin, rectIntersection} from '@dnd-kit/core'

// Mirrors Previews.js: dev talks to the backend on the registry port (__DEV_BACKEND_PORT__), prod to moxtest.app.
const getBackendUrl = () => (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://localhost:${__DEV_BACKEND_PORT__}` : 'https://moxtest.app')

const c = {bg: '#1a1a1a', panel: '#333', panel2: '#444', text: '#fff', textDim: '#888', accent: '#4da3ff', danger: '#ff6b6b'}

let uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

// Card size is computed at runtime (see CARD_W inside the component) so the columns
// always fill the panel edge-to-edge. Scryfall art keeps a 488x680 ratio.
const COLS = 5
const GAP = 6 // horizontal gap between columns (must match s.columns gap)

// A deck's columns. Index 0 is the commander slot; the rest auto-file by type. A column
// with `auto` claims cards of that type ('rest' = catch-all for artifacts / enchantments /
// planeswalkers / anything else); a column without `auto` is a custom column that only holds
// cards dragged into it. Stored per-deck (deck.cats) once edited; otherwise these defaults.
const DEFAULT_CATS = [
  {key: 'commander', label: 'Commander', auto: 'commander'},
  {key: 'lands', label: 'Lands', auto: 'land'},
  {key: 'creatures', label: 'Creatures', auto: 'creature'},
  {key: 'instants', label: 'Instants', auto: 'instant'},
  {key: 'sorceries', label: 'Sorceries', auto: 'sorcery'},
  {key: 'artench', label: 'Art & Ench', auto: 'rest'},
]
const TYPE_OF = {land: 'land', creature: 'creature', instant: 'instant', sorcery: 'sorcery'}

// Formats in the card-detail legality grid (Scryfall keys). Interleaved so a 2-col grid reads down each
// column like Scryfall's card page.
const LEGALITY_FORMATS = [
  ['standard', 'Standard'], ['alchemy', 'Alchemy'],
  ['pioneer', 'Pioneer'], ['historic', 'Historic'],
  ['modern', 'Modern'], ['brawl', 'Brawl'],
  ['legacy', 'Legacy'], ['standardbrawl', 'Std. Brawl'],
  ['vintage', 'Vintage'], ['timeless', 'Timeless'],
  ['commander', 'Commander'], ['pauper', 'Pauper'],
  ['oathbreaker', 'Oathbreaker'], ['penny', 'Penny'],
]
// Badge background + label for a format's status. Commander shows LEGAL/GC when the card is on the
// Commander "Game Changer" list (Scryfall `game_changer`).
const legalityBadge = (key, status, gameChanger) => {
  if (key === 'commander' && status === 'legal' && gameChanger) return {bg: '#5aa83f', label: 'LEGAL/GC'}
  if (status === 'legal') return {bg: '#4a7a4a', label: 'LEGAL'}
  if (status === 'banned') return {bg: '#8a4a4a', label: 'BANNED'}
  if (status === 'restricted') return {bg: '#8a6a30', label: 'RESTRICTED'}
  return {bg: '#4d4d4d', label: 'NOT LEGAL'}
}
// A card's automatic column: the commander goes to the commander slot; everything else to
// the first matching type column, falling back to the 'rest' catch-all (else the last column).
// Land is checked before creature (via column order) so a Land Creature files under Lands.
const categoryIndex = (card, cats, commanderName) => {
  if (commanderName && card.name === commanderName) {
    let ci = cats.findIndex(x => x.auto === 'commander')
    if (ci >= 0) return ci
  }
  let t = (card.type_line || '').toLowerCase()
  for (let i = 0; i < cats.length; i++) {
    let a = cats[i].auto
    if (a && TYPE_OF[a] && t.includes(TYPE_OF[a])) return i
  }
  let rest = cats.findIndex(x => x.auto === 'rest')
  return rest >= 0 ? rest : cats.length - 1
}
const colorRank = card => {
  let cols = card.colors || []
  if (!cols.length) return 6
  if (cols.length > 1) return 5
  return {W: 0, U: 1, B: 2, R: 3, G: 4}[cols[0]] ?? 5
}
// USD price as a number (0 when unknown), used by the price sort.
const priceOf = card => parseFloat(card?.prices?.usd || 0) || 0
// A basic land (incl. snow basics / Wastes) — omitted from the deck's total price (effectively free).
const isBasicLand = card => {
  let t = (card?.type_line || '').toLowerCase()
  return t.includes('basic') && t.includes('land')
}

// A card's effective column: a manual drag override (card.col) wins, else its type bucket.
const effectiveCol = (card, cats, commanderName) => {
  let col = card.col
  if (typeof col === 'number' && col >= 0 && col < cats.length) return col
  return categoryIndex(card, cats, commanderName)
}

// Thin render-prop wrappers so tiles/columns can use dnd-kit hooks without breaking the
// rules of hooks inside the .map() render loops.
const Draggable = ({id, children}) => {
  const {attributes, listeners, setNodeRef, isDragging} = useDraggable({id})
  return children({attributes, listeners, setNodeRef, isDragging})
}
const Droppable = ({id, children}) => {
  const {setNodeRef, isOver} = useDroppable({id})
  return children({setNodeRef, isOver})
}

// Collision detection. A dragged category's node is card-sized, so plain rectIntersection would
// always favor a full column over the thin between-column insertion strips. For category drags we
// first look for an insertion gap the pointer is literally inside; only if there's none do we fall
// back to the normal rectIntersection (columns / rail-tails / +column), so stacking is unchanged.
const catAwareCollision = args => {
  if (String(args.active?.id || '').startsWith('cat-')) {
    let gapHits = pointerWithin({...args, droppableContainers: args.droppableContainers.filter(d => String(d.id).startsWith('gap-'))})
    if (gapHits.length) return gapHits
  }
  return rectIntersection(args)
}

// Admin-selectable AI models per provider (cheapest first). IDs verified against provider docs Aug 2026.
const OPENAI_MODELS = [['gpt-5.6-luna', 'GPT-5.6 Luna (cheapest)'], ['gpt-5.6-terra', 'GPT-5.6 Terra'], ['gpt-5.6-sol', 'GPT-5.6 Sol'], ['gpt-5.2', 'GPT-5.2 (legacy)']]
const ANTHROPIC_MODELS = [['claude-haiku-4-5-20251001', 'Haiku 4.5 (cheapest)'], ['claude-sonnet-5', 'Sonnet 5'], ['claude-opus-5', 'Opus 5'], ['claude-fable-5', 'Fable 5 (top tier)']]
const DEEPSEEK_MODELS = [['deepseek-v4-flash', 'V4 Flash (cheapest)'], ['deepseek-v4-pro', 'V4 Pro']]
const MODELS_BY_PROVIDER = {openai: OPENAI_MODELS, anthropic: ANTHROPIC_MODELS, deepseek: DEEPSEEK_MODELS}
const AI_PROVIDER_LIST = ['openai', 'anthropic', 'deepseek']
const AI_MODEL_DEFAULT = {openai: 'gpt-5.6-terra', anthropic: 'claude-haiku-4-5-20251001', deepseek: 'deepseek-v4-flash'}

let DeckPanel = ({linkDeckId, linkTick, hasSelectedTile, selectedTile, onLinkDeck, onAddCommanderTile, readOnly, viewDecks}) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [decks, setDecks] = useState([])
  const [activeDeckId, setActiveDeckId] = useState(() => {
    try {
      return localStorage.getItem('blogActiveDeckId') || null
    } catch (e) {
      return null
    }
  })
  const [expanded, setExpanded] = useState(null) // deckCardId being hovered/expanded
  const [pendingCommander, setPendingCommander] = useState(null) // {...commander, deckId} after import with no tile selected
  // Column sort: 'cmc' (mana value ascending) or 'price' (most expensive first). Persisted.
  const [sortMode, setSortMode] = useState(() => {
    try {
      return localStorage.getItem('blogDeckSort') || 'cmc'
    } catch (e) {
      return 'cmc'
    }
  })
  // Deck card zoom: the ideal column width — bigger = fewer columns per row = bigger cards (they wrap). Persisted.
  const [deckColW, setDeckColW] = useState(() => {
    try { let v = parseInt(localStorage.getItem('blogDeckColW') || '', 10); if (v > 0) return Math.min(220, Math.max(70, v)) } catch (e) {}
    return 110
  })
  // Optimize printings: swap every card to its cheapest USD printing (whole deck).
  const [optimizing, setOptimizing] = useState(false)
  const [optimizeProgress, setOptimizeProgress] = useState({current: 0, total: 0, saved: 0})
  // Search box to add individual cards to the active deck (resolves via /previews/search).
  const [addQuery, setAddQuery] = useState('')
  const [addResults, setAddResults] = useState([])
  const [addSearching, setAddSearching] = useState(false)

  const [showImport, setShowImport] = useState(false)
  const [importText, setImportText] = useState('')
  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState(null)
  const [importWarning, setImportWarning] = useState(null) // {missing: [names]} for cards that couldn't be resolved
  const [exported, setExported] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [justAddedName, setJustAddedName] = useState(null) // last card added via search/AI → gold border until you click away
  // Admin-gated AI suggestions
  const [showAi, setShowAi] = useState(() => { try { return localStorage.getItem('blogShowAi') === '1' } catch (e) { return false } })
  const [isAdmin, setIsAdmin] = useState(() => { try { return (localStorage.getItem('adminToken') || '').startsWith('admin-') } catch (e) { return false } })
  const [adminPass, setAdminPass] = useState('')
  const [adminError, setAdminError] = useState(false)
  const [adminChecking, setAdminChecking] = useState(false)
  const [providerKeys, setProviderKeys] = useState(null) // {openai,anthropic,deepseek}: which providers have a saved key
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [apiKeySaving, setApiKeySaving] = useState(false)
  const [aiProvider, setAiProvider] = useState('openai') // 'openai' | 'anthropic' — which key you're adding
  const [aiModel, setAiModel] = useState(() => { try { return localStorage.getItem('blogAiModel') || '' } catch (e) { return '' } }) // admin model pick, remembered across reloads
  const [aiCardW, setAiCardW] = useState(() => { try { return parseInt(localStorage.getItem('blogAiCardW') || '', 10) || 150 } catch (e) { return 150 } }) // suggestion-card size
  const [editKey, setEditKey] = useState(false) // force the key-entry UI even when a key is already stored
  const [keyError, setKeyError] = useState(null) // provider validation error shown under the key input
  const [aiQuestion, setAiQuestion] = useState('')
  const [aiAsking, setAiAsking] = useState(false)
  const aiAbortRef = useRef(null) // AbortController for the in-flight AI request, so Stop can cancel it
  const audioCtxRef = useRef(null) // lazily-created AudioContext for the "done" chime
  const autoOptimizeRef = useRef(false) // kick off the price optimizer once after an AI full-deck import
  const [aiEffort, setAiEffort] = useState('high')
  const [aiCards, setAiCards] = useState([])
  const [aiCardsLoading, setAiCardsLoading] = useState(false)
  const [aiBuilding, setAiBuilding] = useState(null) // {current,total} while a full-deck AI response is resolved + imported
  const [aiStage, setAiStage] = useState('') // cycling status hint while the AI request is in flight (Thinking → Checking prices → Finalizing)
  useEffect(() => {
    if (!aiAsking) { setAiStage(''); return }
    let stages = ['Thinking…', 'Checking prices…', 'Finalizing…']
    let i = 0
    setAiStage(stages[0])
    let t = setInterval(() => { i = Math.min(i + 1, stages.length - 1); setAiStage(stages[i]) }, 6000)
    return () => clearInterval(t)
  }, [aiAsking])
  const [aiError, setAiError] = useState(null)
  const [editingName, setEditingName] = useState(false)
  const [showDeckList, setShowDeckList] = useState(false)
  const [nameInput, setNameInput] = useState('')

  // Inline login (deckUser session, same one the main app uses)
  const [authMode, setAuthMode] = useState('login')
  const [authUser, setAuthUser] = useState('')
  const [authPass, setAuthPass] = useState('')
  const [authError, setAuthError] = useState('')

  const didLoad = useRef(false)

  // Measure the panel's inner width so cards can scale to fill the (resizable) rail.
  const panelRef = useRef(null)
  const [innerW, setInnerW] = useState(760)
  useEffect(() => {
    let el = panelRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    let update = () => setInnerW(el.clientWidth)
    update()
    let ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Drag deck tiles between the type columns (a manual override that persists with the deck).
  const dndSensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 5}}))
  const [activeDragId, setActiveDragId] = useState(null)

  // Custom-column editing + masonry layout (columns tuck under the shortest, like the
  // main deck builder). railRefs measure each rail's height; colPos holds computed x/y per rail.
  const [focusColKey, setFocusColKey] = useState(null) // newly-added column to autofocus
  const railRefs = useRef({}) // rail's first-cat-key -> rail DOM node, for height measurement
  const [colPos, setColPos] = useState([]) // computed {x,y} per rail
  const [masonryH, setMasonryH] = useState(0)

  // Settings: hover-to-expand (default) vs click-to-expand cards (for browsers that handle hover
  // poorly). Persisted. `ghost` holds a just-deleted card's slot as a blank placeholder until the
  // user stops hovering/expanding it, so the column doesn't jump out from under the cursor.
  const [expandMode, setExpandMode] = useState(() => { try { return localStorage.getItem('blogExpandMode') || 'hover' } catch (e) { return 'hover' } })
  const [showSettings, setShowSettings] = useState(false)
  const [catSettingsKey, setCatSettingsKey] = useState(null) // category whose settings cog popover is open
  const [ghost, setGhost] = useState(null) // {stackId, id}
  useEffect(() => { try { localStorage.setItem('blogExpandMode', expandMode) } catch (e) {} }, [expandMode])
  const [undoStack, setUndoStack] = useState([]) // snapshots of the active deck before structural edits
  const [undoSpin, setUndoSpin] = useState(0) // drives the undo button's counterclockwise spin

  // Read-only (viewing a shared tier list): the decks come from the owner's fetched data, never from
  // this viewer's own account/localStorage. Keep them in sync as the parent's viewDecks arrives.
  useEffect(() => {
    if (readOnly) setDecks(Array.isArray(viewDecks) ? viewDecks : [])
  }, [readOnly, viewDecks])

  // On mount: who am I, and load my decks (server if logged in, else localStorage).
  useEffect(() => {
    if (readOnly) { setAuthChecked(true); return } // don't touch this viewer's own decks in view mode
    let cancelled = false
    ;(async () => {
      try {
        let meRes = await fetch('/api/auth/me', {credentials: 'include'})
        let me = await meRes.json()
        if (cancelled) return
        if (me.loggedIn) {
          setCurrentUser(me.user)
          let dRes = await fetch('/api/decks', {credentials: 'include'})
          let d = await dRes.json()
          if (!cancelled) setDecks(d.decks || [])
        } else {
          try {
            let raw = localStorage.getItem('mtgDecks')
            if (raw) setDecks(JSON.parse(raw))
          } catch (e) {}
        }
      } catch (e) {
      } finally {
        if (!cancelled) setAuthChecked(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Persist on every change — server for logged-in users, localStorage otherwise.
  // Skip the very first run so we don't echo freshly-loaded data back.
  useEffect(() => {
    if (readOnly) return // never write a viewer's account/localStorage from a shared view
    if (!authChecked) return
    if (!didLoad.current) {
      didLoad.current = true
      return
    }
    if (currentUser) {
      fetch('/api/decks', {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({decks})}).catch(() => {})
    } else {
      try {
        localStorage.setItem('mtgDecks', JSON.stringify(decks))
      } catch (e) {}
    }
  }, [decks, authChecked, currentUser])

  // If the active id points to a deleted deck, fall back to the first — but allow a deliberate
  // null (empty panel after clicking a deckless tile), and don't wipe a saved id before load.
  useEffect(() => {
    if (activeDeckId && decks.length > 0 && !decks.find(d => d.id === activeDeckId)) setActiveDeckId(decks[0].id)
  }, [decks, activeDeckId])

  // Remember the open deck so a reload / return reopens it.
  useEffect(() => {
    if (readOnly) return // don't stamp the viewer's localStorage with the owner's deck id
    try {
      if (activeDeckId) localStorage.setItem('blogActiveDeckId', activeDeckId)
    } catch (e) {}
  }, [activeDeckId])

  // Remember the chosen column sort.
  useEffect(() => {
    try {
      localStorage.setItem('blogDeckSort', sortMode)
    } catch (e) {}
  }, [sortMode])

  // Remember the deck card zoom.
  useEffect(() => {
    try {
      localStorage.setItem('blogDeckColW', String(deckColW))
    } catch (e) {}
  }, [deckColW])

  // Debounced card search for the "add a card" box (same resolver as import).
  useEffect(() => {
    if (!addQuery.trim()) {
      setAddResults([])
      return
    }
    let cancelled = false
    setAddSearching(true)
    let t = setTimeout(async () => {
      try {
        let res = await fetch(`${getBackendUrl()}/previews/search?q=${encodeURIComponent(addQuery)}`)
        let data = res.ok ? await res.json() : {results: []}
        if (!cancelled) setAddResults((data.results || []).slice(0, 8))
      } catch (e) {
        if (!cancelled) setAddResults([])
      } finally {
        if (!cancelled) setAddSearching(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [addQuery])

  // When a tile is clicked: show its linked deck, or clear the panel if it has none.
  // Clearing happens only on an actual click (linkTick change), not on deck edits, so a
  // manual dropdown pick and the restored-on-load deck aren't wiped by unrelated changes.
  const prevTick = useRef(linkTick)
  useEffect(() => {
    let clicked = linkTick !== prevTick.current
    prevTick.current = linkTick
    if (linkDeckId && decks.find(d => d.id === linkDeckId)) setActiveDeckId(linkDeckId)
    else if (clicked) setActiveDeckId(null)
  }, [linkDeckId, linkTick, decks])

  // Opening a tile that has no linked deck spins up an empty deck for it, so it shows the normal
  // (but empty) deck view with all header controls — you can search or import straight from there.
  // Gate on authChecked: until /api/decks (or localStorage) has loaded, `decks` is still [] and the
  // guards below would all miss — so on every refresh we'd spawn a duplicate empty deck and re-link
  // the selected tile to it, orphaning the real deck. Also bail if the tile is already tied to a
  // loaded deck (linkDeckId), which is the case being restored on reload.
  useEffect(() => {
    if (readOnly) return // a viewer never spins up decks
    if (!authChecked) return // decks not loaded yet — don't create against an empty list
    if (!selectedTile) return
    if (linkDeckId && decks.some(d => d.id === linkDeckId)) return // tile already tied to a real deck
    if (activeDeckId && decks.some(d => d.id === activeDeckId)) return
    let id = Date.now().toString()
    let newDeck = {id, name: selectedTile.name || `Deck ${decks.length + 1}`, createdAt: new Date().toISOString(), commander: {name: selectedTile.name, url: selectedTile.art || null}, stacks: [{id: 'main', name: 'Main', cards: []}]}
    setDecks(ds => [...ds, newDeck])
    setActiveDeckId(id)
    if (hasSelectedTile) onLinkDeck?.(id, newDeck.commander)
  }, [selectedTile, activeDeckId, authChecked, linkDeckId])

  let activeDeck = decks.find(d => d.id === activeDeckId) || null
  let flat = activeDeck ? activeDeck.stacks.flatMap(s => s.cards.map(card => ({card, stackId: s.id}))) : []
  let countByName = name => flat.filter(x => x.card.name === name).length

  // The active deck's columns (custom edits persist on the deck; else the defaults) and its
  // commander name (the commander tile is pinned to the commander column).
  let commanderName = activeDeck?.commander?.name || null
  let cats = activeDeck?.cats?.length ? activeDeck.cats : DEFAULT_CATS

  let deckTotal = flat.reduce((sum, x) => {
    if (isBasicLand(x.card)) return sum // basics are effectively free
    if (cats[effectiveCol(x.card, cats, commanderName)]?.excludePrice) return sum // category opted out of the total
    return sum + priceOf(x.card)
  }, 0) // whole-deck USD value; basics and price-excluded categories omitted

  // The column layout: an ordered list of rails, each an ordered list of category keys stacked
  // vertically (solitaire-style). Absent -> every category is its own rail. Sanitized so every
  // current category appears exactly once (new cats -> own rail; removed cats dropped).
  let rails = (() => {
    let known = new Set(cats.map(x => x.key))
    let seen = new Set()
    let out = (activeDeck?.layout || [])
      .map(rail => (rail || []).filter(k => known.has(k) && !seen.has(k) && (seen.add(k), true)))
      .filter(r => r.length)
    cats.forEach(x => { if (!seen.has(x.key)) out.push([x.key]) })
    return out.length ? out : cats.map(x => [x.key])
  })()
  let catIndexByKey = key => cats.findIndex(x => x.key === key)

  // Undo: snapshot the active deck before each structural edit (delete/add category, drag/stack,
  // re-file, +/- copies); undo restores the last snapshot. Snapshots are cheap — our updates are
  // immutable, so the old deck object is a valid frozen copy.
  let pushUndo = () => activeDeck && setUndoStack(s => [...s.slice(-24), {id: activeDeck.id, snap: activeDeck}])
  let undo = () => {
    if (!undoStack.length) return
    let last = undoStack[undoStack.length - 1]
    setDecks(ds => ds.map(d => (d.id === last.id ? last.snap : d)))
    setUndoStack(s => s.slice(0, -1))
  }

  // Bucket cards into the columns, each sorted by mana value then color.
  let columns = cats.map(() => [])
  flat.forEach(entry => columns[effectiveCol(entry.card, cats, commanderName)].push(entry))
  let byCmc = (a, b) => (a.card.cmc || 0) - (b.card.cmc || 0) || colorRank(a.card) - colorRank(b.card)
  let byPrice = (a, b) => priceOf(b.card) - priceOf(a.card) || byCmc(a, b) // most expensive first
  columns.forEach(col => col.sort(sortMode === 'price' ? byPrice : byCmc))
  // Collapse duplicates: one tile per card name, carrying the count and every copy's id.
  let groupColumn = col => {
    let out = []
    let byName = {}
    col.forEach(({card, stackId}) => {
      let g = byName[card.name]
      if (!g) {
        g = {card, stackId, ids: []}
        byName[card.name] = g
        out.push(g)
      }
      g.ids.push(card.deckCardId)
    })
    return out
  }
  let groupedColumns = columns.map(groupColumn)

  // Commander portrait: the live commander card (reflects art picks), else the first card.
  let portraitCard = null
  if (activeDeck) {
    let cn = activeDeck.commander?.name
    if (cn) {
      for (let st of activeDeck.stacks) {
        let f = st.cards.find(cd => cd.name === cn)
        if (f) {
          portraitCard = f
          break
        }
      }
    }
    if (!portraitCard) portraitCard = activeDeck.commander || activeDeck.stacks[0]?.cards[0] || null
  }
  // Prefer the art crop (just the art). Use scryfallId, else set/collector; last resort the full card.
  let portraitSrc = null
  if (portraitCard) {
    if (portraitCard.scryfallId) portraitSrc = `https://api.scryfall.com/cards/${portraitCard.scryfallId}?format=image&version=art_crop`
    else if (portraitCard.setCode && portraitCard.collector_number) portraitSrc = `https://api.scryfall.com/cards/${String(portraitCard.setCode).toLowerCase()}/${portraitCard.collector_number}?format=image&version=art_crop`
    else portraitSrc = portraitCard.url
  }
  // No deck yet, but a tier-list tile is selected → show that tile's art as the portrait.
  if (!portraitSrc && !activeDeck && selectedTile?.art) portraitSrc = selectedTile.art
  // A tile is selected but has no deck linked yet: show an empty state with an import prompt.
  let emptyTile = !activeDeck && !!selectedTile

  let handleAuth = async e => {
    e.preventDefault()
    setAuthError('')
    try {
      let res = await fetch(`/api/auth/${authMode}`, {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({username: authUser, password: authPass})})
      let data = await res.json()
      if (!res.ok || !data.success) {
        setAuthError(data.error || 'Login failed')
        return
      }
      setCurrentUser(data.user)
      setAuthPass('')
      let dRes = await fetch('/api/decks', {credentials: 'include'})
      let d = await dRes.json()
      setDecks(d.decks || [])
    } catch (err) {
      setAuthError('Network error')
    }
  }

  let handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {method: 'POST', credentials: 'include'})
    } catch (e) {}
    setCurrentUser(null)
    setDecks([])
  }

  // A single name lookup: prefer exact match, then prefix, then first result. null on miss/error.
  let resolveCard = async (backend, query) => {
    try {
      let res = await fetch(`${backend}/previews/search?q=${encodeURIComponent(query)}`)
      if (!res.ok) return null
      let data = await res.json()
      let results = data.results || []
      let q = query.toLowerCase()
      return results.find(r => r.name.toLowerCase() === q) || results.find(r => r.name.toLowerCase().startsWith(q)) || results[0] || null
    } catch (e) {
      return null
    }
  }

  // Parity with the old builder: if a card's image is still a Scryfall CDN URL, download it to the
  // server cache (POST /previews/cache-card) and swap in the local /card/... URL, so the tier list
  // stops hotlinking Scryfall. No-op for cards already served locally or without a set code. Never
  // throws — on any failure the card keeps its Scryfall URL.
  let cacheScryfallCard = async (backend, card) => {
    if (!card) return card
    let img = card.url || card.scryfallUrl
    if (!img || !(img.startsWith('https://cards.scryfall.io/') || img.startsWith('https://api.scryfall.com/'))) return card
    if (!card.name || !card.setCode) return card // cache-card requires name + setCode
    try {
      let res = await fetch(`${backend}/previews/cache-card`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          name: card.name, setCode: card.setCode, collector_number: card.collector_number,
          scryfallUrl: img, colors: card.colors, cmc: card.cmc, type_line: card.type_line,
          mana_cost: card.mana_cost, rarity: card.rarity, prices: card.prices,
        }),
      })
      let data = res.ok ? await res.json() : null
      if (data && data.localUrl) return {...card, url: data.localUrl, source: 'local'}
    } catch (e) {}
    return card
  }

  // Import a pasted decklist -> resolve each unique name via /previews/search -> new deck.
  // Dedupes identical names and retries misses: a 100-card burst otherwise trips Scryfall's
  // ~10 req/s limit on localhost and silently loses whatever got 429'd.
  let importDeck = async () => {
    let lines = importText
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean)
    let order = [] // unique queries, first-seen order
    let qtyByQuery = new Map() // query -> total copies wanted
    let section = null
    let commanderName = null
    let firstName = null
    let total = 0
    for (let line of lines) {
      // Only real card lines start with a quantity; other lines are section headers.
      let m = line.match(/^(\d+)x?\s+(.+)$/i)
      if (!m) {
        section = line.toLowerCase().replace(/[^a-z]/g, '')
        continue
      }
      let qty = Math.min(parseInt(m[1], 10) || 1, 99)
      // Strip exporter cruft: " (SET) 123" and any trailing tokens (foil markers, etc.).
      let name = m[2]
        .replace(/\s+\([A-Za-z0-9]{2,6}\)\s+.*$/, '')
        .replace(/\s*\*[^*]*\*\s*$/, '')
        .trim()
      if (!name) continue
      if (!firstName) firstName = name
      if (section === 'commander' && !commanderName) commanderName = name
      // For split / double-faced cards, search the front face. Strip Arena's Alchemy
      // "A-" rebalance prefix so it resolves to the normal paper printing.
      let query = name.split('//')[0].trim().replace(/^A-/, '')
      if (total >= 1000) continue
      if (!qtyByQuery.has(query)) order.push(query)
      qtyByQuery.set(query, (qtyByQuery.get(query) || 0) + qty)
      total += qty
    }
    if (!order.length) return
    if (!commanderName) commanderName = firstName

    setImporting(true)
    setImportWarning(null)
    setImportProgress({current: 0, total: order.length})
    let backend = getBackendUrl()
    let resolved = new Map() // query -> card

    // Resolve uniques in gentle batches (< 10/s), retrying misses a few rounds to ride out 429s.
    let queue = [...order]
    let BATCH = 4
    for (let round = 0; round < 3 && queue.length; round++) {
      let next = []
      for (let i = 0; i < queue.length; i += BATCH) {
        let batch = queue.slice(i, i + BATCH)
        let cards = await Promise.all(batch.map(q => resolveCard(backend, q)))
        batch.forEach((q, j) => (cards[j] ? resolved.set(q, cards[j]) : next.push(q)))
        setImportProgress({current: resolved.size, total: order.length})
        await new Promise(r => setTimeout(r, 550))
      }
      queue = next
      if (queue.length) await new Promise(r => setTimeout(r, 1500)) // cool-down before retrying misses
    }

    // Cache any Scryfall-hosted images to the server (parity with the old builder) so the deck's art
    // is served locally instead of hotlinked from Scryfall's CDN. Only genuinely-new cards do work
    // here — anything already in a synced set came back as source:'local'. Batched, best-effort.
    let uniqueQueries = [...resolved.keys()]
    for (let i = 0; i < uniqueQueries.length; i += 4) {
      let batch = uniqueQueries.slice(i, i + 4)
      await Promise.all(batch.map(async q => resolved.set(q, await cacheScryfallCard(backend, resolved.get(q)))))
    }

    // Expand uniques back into individual copies, in list order.
    let found = []
    for (let query of order) {
      let card = resolved.get(query)
      if (!card) continue
      let n = qtyByQuery.get(query)
      for (let k = 0; k < n; k++) found.push({...card, deckCardId: uid()})
    }
    let missing = order.filter(q => !resolved.has(q))

    // Identify the commander so the deck can be tied to a tier-list tile.
    let commander = null
    if (commanderName) {
      let cn = commanderName.toLowerCase()
      let front = cn.split('//')[0].trim()
      commander = found.find(x => x.name.toLowerCase() === cn) || found.find(x => x.name.toLowerCase().startsWith(front)) || null
    }

    setImporting(false)
    setImportProgress(null)
    setShowImport(false)
    setImportText('')
    // Surface anything that still couldn't be found so nothing vanishes silently.
    setImportWarning(missing.length ? {missing} : null)

    // Reuse the currently-open empty deck (auto-created for this tile) instead of spawning a duplicate.
    let openEmpty = decks.find(d => d.id === activeDeckId && d.stacks.every(s => s.cards.length === 0))
    if (openEmpty) {
      setDecks(ds => ds.map(d => (d.id === openEmpty.id ? {...d, name: commander ? commander.name : d.name, commander: commander || d.commander, stacks: [{id: 'imported', name: 'Imported', cards: found}]} : d)))
      if (hasSelectedTile) onLinkDeck?.(openEmpty.id, commander || openEmpty.commander)
      return
    }

    let newDeck = {
      id: Date.now().toString(),
      name: commander ? commander.name : `Imported Deck ${decks.length + 1}`,
      createdAt: new Date().toISOString(),
      commander: commander || null,
      stacks: [{id: 'imported', name: 'Imported', cards: found}],
    }
    setDecks(ds => [...ds, newDeck])
    setActiveDeckId(newDeck.id)
    // Tie the deck to a tier-list tile: the selected one, or offer the commander as a new tile.
    if (hasSelectedTile) onLinkDeck?.(newDeck.id, commander)
    else if (commander) setPendingCommander({...commander, deckId: newDeck.id})
  }

  let mutateStack = (stackId, fn) =>
    setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, stacks: d.stacks.map(s => (s.id === stackId ? {...s, cards: fn(s.cards)} : s))} : d)))

  let addCopy = (stackId, card) => { pushUndo(); mutateStack(stackId, cards => [...cards, {...card, deckCardId: uid()}]) }
  let removeOne = (stackId, deckCardId) => { pushUndo(); mutateStack(stackId, cards => cards.filter(x => x.deckCardId !== deckCardId)) }

  // Add a searched card as a new copy into the deck's first stack (it buckets by type). On an empty
  // tile (no deck yet) this spins up a fresh deck linked to that tile, so you can start by hand.
  let addCardToDeck = async card => {
    setJustAddedName(card.name) // flash a gold border on the freshly-added tile
    card = await cacheScryfallCard(getBackendUrl(), card) // cache its image to the server if it's still on a Scryfall URL
    if (!activeDeck) {
      if (!emptyTile) return
      let id = Date.now().toString()
      let newDeck = {
        id,
        name: selectedTile?.name || `Deck ${decks.length + 1}`,
        createdAt: new Date().toISOString(),
        commander: selectedTile ? {name: selectedTile.name, url: selectedTile.art || null} : null,
        stacks: [{id: 'main', name: 'Main', cards: [{...card, deckCardId: uid()}]}],
      }
      setDecks(ds => [...ds, newDeck])
      setActiveDeckId(id)
      if (hasSelectedTile) onLinkDeck?.(id, newDeck.commander)
      setAddQuery('')
      setAddResults([])
      return
    }
    let stackId = activeDeck.stacks[0]?.id
    if (!stackId) return
    mutateStack(stackId, cards => [...cards, {...card, deckCardId: uid()}])
    setAddQuery('')
    setAddResults([])
  }

  // Pin every copy of a card to a chosen column (drag override); saved with the deck.
  let moveCardToColumn = (name, col) => {
    pushUndo()
    setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, stacks: d.stacks.map(s => ({...s, cards: s.cards.map(x => (x.name === name ? {...x, col} : x))}))} : d)))
  }

  // Column (category) editing. The first edit materializes the defaults onto the deck.
  let saveCats = next => setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, cats: next} : d)))
  let addCategory = () => {
    pushUndo()
    let key = `custom_${uid()}`
    saveCats([...cats, {key, label: ''}]) // empty label -> placeholder; user types the name
    setFocusColKey(key)
  }
  let renameCategory = (key, label) => saveCats(cats.map(x => (x.key === key ? {...x, label} : x)))
  // Toggle whether a category's cards count toward the deck-total price (off = like a sideboard).
  let setCatExclude = (key, excluded) => { pushUndo(); saveCats(cats.map(x => (x.key === key ? {...x, excludePrice: excluded} : x))) }
  let setCatExportExclude = (key, excluded) => { pushUndo(); saveCats(cats.map(x => (x.key === key ? {...x, excludeExport: excluded} : x))) }
  // Delete any category: drop it, un-pin cards filed there, and shift higher pins down one. Its
  // cards re-file by type (a deleted type column's cards fall to the catch-all). At least one stays.
  let deleteCategory = key => {
    let idx = cats.findIndex(x => x.key === key)
    if (idx < 0 || cats.length <= 1) return
    pushUndo()
    let fix = col => (typeof col !== 'number' ? col : col === idx ? undefined : col > idx ? col - 1 : col)
    setDecks(ds =>
      ds.map(d =>
        d.id === activeDeckId
          ? {...d, cats: cats.filter(x => x.key !== key), stacks: d.stacks.map(st => ({...st, cards: st.cards.map(cd => ({...cd, col: fix(cd.col)}))}))}
          : d,
      ),
    )
  }

  // Solitaire column layout: stack a category to the bottom of another's rail, or pull it out to
  // its own rail. Fewer rails => wider cards. Persisted as deck.layout.
  let saveLayout = next => setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, layout: next} : d)))
  let stackCatUnder = (dragKey, targetKey) => {
    if (dragKey === targetKey) return
    pushUndo()
    let next = rails.map(r => r.filter(k => k !== dragKey)).filter(r => r.length)
    let ti = next.findIndex(r => r.includes(targetKey))
    if (ti < 0) { next.push([dragKey]); saveLayout(next); return }
    let pos = next[ti].indexOf(targetKey) // insert directly under the hovered category
    next[ti] = [...next[ti].slice(0, pos + 1), dragKey, ...next[ti].slice(pos + 1)]
    saveLayout(next)
  }
  let moveCatToOwnRail = dragKey => {
    pushUndo()
    let next = rails.map(r => r.filter(k => k !== dragKey)).filter(r => r.length)
    next.push([dragKey])
    saveLayout(next)
  }
  // Append a category to the bottom of the rail that starts with railFirstKey (used when dropping
  // into the empty space beneath a column).
  let appendToRail = (dragKey, railFirstKey) => {
    pushUndo()
    let next = rails.map(r => r.filter(k => k !== dragKey)).filter(r => r.length)
    let ti = next.findIndex(r => r.includes(railFirstKey))
    if (ti < 0) next.push([dragKey])
    else next[ti] = [...next[ti], dragKey]
    saveLayout(next)
  }
  // Pull a category out into its OWN new rail, inserted just before the rail whose first category
  // is beforeFirstKey (null => append at the end). This is the "drop in the gap between two
  // columns to spawn a new column right there" gesture, which also lets you reorder columns.
  let insertCatAtGap = (dragKey, beforeFirstKey) => {
    if (dragKey === beforeFirstKey) return // gap hugging the dragged category's own column
    let next = rails.map(r => r.filter(k => k !== dragKey)).filter(r => r.length)
    let idx = beforeFirstKey == null ? -1 : next.findIndex(r => r[0] === beforeFirstKey)
    if (idx < 0) next.push([dragKey])
    else next.splice(idx, 0, [dragKey])
    if (JSON.stringify(next) === JSON.stringify(rails)) return // no-op drop
    pushUndo()
    saveLayout(next)
  }

  // Current price for one card via the backend (cached 24h server-side across all users).
  let fetchCardPrice = async card => {
    if (!card.scryfallId) return null
    try {
      let res = await fetch(`${getBackendUrl()}/previews/price/${encodeURIComponent(card.scryfallId)}`)
      if (!res.ok) return null
      return (await res.json()).prices || null
    } catch (e) {
      return null
    }
  }
  // On opening a deck, quietly refresh prices for any card not priced in the last 24h (stamped per
  // card so re-opening within a day does nothing). Throttled; the server cache bounds Scryfall load.
  useEffect(() => {
    if (!activeDeck) return
    let DAY = 24 * 60 * 60 * 1000
    let now = Date.now()
    let byName = {}
    activeDeck.stacks.forEach(st => st.cards.forEach(cd => { if (!byName[cd.name]) byName[cd.name] = cd }))
    if (activeDeck.commander && !byName[activeDeck.commander.name]) byName[activeDeck.commander.name] = activeDeck.commander
    let stale = Object.values(byName).filter(cd => cd.scryfallId && now - (cd.pricedAt ? Date.parse(cd.pricedAt) : 0) > DAY)
    if (!stale.length) return
    let cancelled = false
    let run = async () => {
      let BATCH = 5
      for (let i = 0; i < stale.length && !cancelled; i += BATCH) {
        let batch = stale.slice(i, i + BATCH)
        let got = await Promise.all(batch.map(cd => fetchCardPrice(cd).then(prices => ({name: cd.name, prices}))))
        if (cancelled) return
        let priceByName = {}
        got.forEach(r => (priceByName[r.name] = r.prices))
        let stamp = new Date().toISOString()
        let names = new Set(batch.map(b => b.name))
        // Stamp every copy (even on a miss) so we don't re-ping for 24h; keep old price if none came back.
        let apply = cd => (names.has(cd.name) ? {...cd, prices: priceByName[cd.name] || cd.prices, pricedAt: stamp} : cd)
        setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, stacks: d.stacks.map(st => ({...st, cards: st.cards.map(apply)})), commander: d.commander ? apply(d.commander) : d.commander} : d)))
        await new Promise(r => setTimeout(r, 300))
      }
    }
    run()
    return () => { cancelled = true }
  }, [activeDeckId])

  // Copy the active deck as a plain "N Name" decklist (available to viewers too, later).
  let exportDeck = () => {
    if (!activeDeck) return
    let counts = {}
    let order = []
    activeDeck.stacks.forEach(st =>
      st.cards.forEach(cd => {
        if (cats[effectiveCol(cd, cats, commanderName)]?.excludeExport) return // category opted out of export
        if (counts[cd.name] === undefined) order.push(cd.name)
        counts[cd.name] = (counts[cd.name] || 0) + 1
      }),
    )
    let text = order.map(n => `${counts[n]} ${n}`).join('\n')
    try {
      navigator.clipboard?.writeText(text)
    } catch (e) {}
    setExported(true)
    setTimeout(() => setExported(false), 1500)
  }

  // Optimize printings: for every unique card, fetch all printings from Scryfall and swap it
  // to the cheapest USD printing. Mirrors the main deck builder (direct Scryfall, 120ms throttle).
  let optimizeDeckPrices = async () => {
    if (!activeDeck || optimizing) return
    // Dedupe by name across all stacks (+ the commander, if it isn't already a stack card).
    let cardsByName = {}
    activeDeck.stacks.forEach(st => st.cards.forEach(card => (cardsByName[card.name] = cardsByName[card.name] || []).push(card)))
    if (activeDeck.commander && !cardsByName[activeDeck.commander.name]) cardsByName[activeDeck.commander.name] = [activeDeck.commander]

    let uniqueNames = Object.keys(cardsByName)
    if (!uniqueNames.length) return

    setOptimizing(true)
    setOptimizeProgress({current: 0, total: uniqueNames.length, saved: 0})

    let totalSaved = 0
    let replacements = {} // name -> cheapest Scryfall printing

    try {
      for (let i = 0; i < uniqueNames.length; i++) {
        let name = uniqueNames[i]
        setOptimizeProgress(p => ({...p, current: i + 1}))
        try {
          let sample = cardsByName[name][0]
          // Printings via our 24h-cached backend endpoint (shared with the art picker) instead of two
          // direct Scryfall calls per card — far fewer API hits, and the cache makes re-runs instant.
          let prints = []
          try {
            let r = await fetch(`${getBackendUrl()}/previews/prints?name=${encodeURIComponent(name)}`)
            if (r.ok) prints = (await r.json()).prints || []
          } catch (e) {}
          if (!prints.length) continue
          let cardData = prints.find(p => p.id === sample.scryfallId) || null // the current printing, for the junk-printing check below
          // Skip gold-bordered (World Championship), Collectors' / International Edition & other
          // memorabilia, oversized, and digital-only printings — they carry junk placeholder
          // prices and aren't real tournament cards, so they shouldn't win the cheapest slot.
          // Fall back to the full list only if a card has no "real paper" printing at all.
          let isRealPaper = v =>
            v.border_color !== 'gold' &&
            v.set_type !== 'memorabilia' &&
            !v.oversized &&
            !v.digital &&
            (!v.games || v.games.includes('paper'))
          let pool = prints.filter(isRealPaper)
          if (!pool.length) pool = prints
          // Cheapest by nonfoil USD (no-price printings sort last via the 999999 sentinel).
          let cheapest = null
          let cheapestPrice = Infinity
          pool.forEach(v => {
            let price = parseFloat(v.prices?.usd || '999999')
            if (price < cheapestPrice) {
              cheapestPrice = price
              cheapest = v
            }
          })
          if (cheapest && cheapestPrice < Infinity) {
            // Replace if the current printing is itself junk (force it off gold/memorabilia), a
            // copy has no price (upgrade it), or a strictly cheaper real printing exists.
            let shouldReplace = !!(cardData && !isRealPaper(cardData))
            cardsByName[name].forEach(card => {
              let cur = parseFloat(card.prices?.usd || '0')
              if (!card.prices?.usd) shouldReplace = true
              else if (cheapestPrice < cur) {
                totalSaved += cur - cheapestPrice
                shouldReplace = true
              }
            })
            if (shouldReplace) replacements[name] = cheapest
          }
        } catch (e) {}
        await new Promise(r => setTimeout(r, 120)) // Scryfall ~10 req/s rate limit
      }

      // Map a chosen Scryfall printing onto our card shape (also fixes collector_number,
      // which the main builder leaves stale).
      let applyVariant = card => {
        let v = replacements[card.name]
        if (!v) return card
        let imageUrl = v.image_uris?.normal || v.image_uris?.small || card.url
        return {...card, url: imageUrl, scryfallUrl: imageUrl, setCode: v.set, setName: v.set_name, collector_number: v.collector_number, prices: v.prices, scryfallId: v.id}
      }
      setDecks(ds =>
        ds.map(d => (d.id === activeDeckId ? {...d, stacks: d.stacks.map(st => ({...st, cards: st.cards.map(applyVariant)})), commander: d.commander ? applyVariant(d.commander) : d.commander} : d)),
      )
      setOptimizeProgress(p => ({...p, saved: totalSaved}))
    } catch (e) {
    } finally {
      setTimeout(() => {
        setOptimizing(false)
        setOptimizeProgress({current: 0, total: 0, saved: 0})
      }, 2000) // hold the final savings figure for a moment
    }
  }

  // After an AI full-deck import lands (the cards just jumped into the deck), auto-run the price
  // optimizer once — no need to click Optimize each time a built deck comes in.
  useEffect(() => {
    if (autoOptimizeRef.current && !optimizing && flat.length > 1) {
      autoOptimizeRef.current = false
      optimizeDeckPrices()
    }
  }, [flat.length, optimizing])

  let onImgError = (e, card) => {
    if (card.scryfallUrl && e.target.src !== card.scryfallUrl) e.target.src = card.scryfallUrl
  }

  // Art picker: clicking a deck card opens a left panel of that card's printings.
  const [artCard, setArtCard] = useState(null) // {stackId, deckCardId, card}
  const [variants, setVariants] = useState([])
  const [loadingVariants, setLoadingVariants] = useState(false)
  const [variantError, setVariantError] = useState(false)
  const [shownCount, setShownCount] = useState(12)
  const [retryTick, setRetryTick] = useState(0)

  useEffect(() => {
    if (!artCard) {
      setVariants([])
      setVariantError(false)
      return
    }
    let cancelled = false
    setLoadingVariants(true)
    setVariantError(false)
    setShownCount(12)
    ;(async () => {
      // Printings come through our backend, which caches the Scryfall prints search 24h (with server-side
      // retry/backoff) — so re-opening a card, or opening one right after an import, no longer trips
      // Scryfall's API rate limit.
      let res = null
      try {
        res = await fetch(`${getBackendUrl()}/previews/prints?name=${encodeURIComponent(artCard.card.name)}`)
      } catch (e) {
        res = null
      }
      if (cancelled) return
      if (!res || !res.ok) {
        setVariants([])
        setVariantError(true)
        setLoadingVariants(false)
        return
      }
      try {
        let data = await res.json()
        let list = (data.prints || [])
          .map(cc => ({
            id: cc.id,
            set: cc.set,
            set_name: cc.set_name,
            collector_number: cc.collector_number,
            image_uri: cc.image_uris?.normal || cc.card_faces?.[0]?.image_uris?.normal || null,
            usd: parseFloat(cc.prices?.usd) || null,
            prices: cc.prices || null,
            // Card detail (for the enriched picker header) — fall back to the front face on DFCs/MDFCs.
            mana_cost: cc.mana_cost || cc.card_faces?.[0]?.mana_cost || '',
            type_line: cc.type_line || cc.card_faces?.[0]?.type_line || '',
            oracle_text: cc.oracle_text || (cc.card_faces ? cc.card_faces.map(f => f.oracle_text).filter(Boolean).join('\n//\n') : '') || '',
            rarity: cc.rarity || '',
            legalities: cc.legalities || null, // per-format legal/not_legal/banned/restricted (card-level; same across printings)
            game_changer: cc.game_changer || false, // on the Commander "Game Changer" list?
            flavor_text: cc.flavor_text || (cc.card_faces ? cc.card_faces.map(f => f.flavor_text).filter(Boolean).join('\n//\n') : '') || '',
          }))
          .filter(v => v.image_uri)
          .sort((a, b) => (a.usd == null ? Infinity : a.usd) - (b.usd == null ? Infinity : b.usd)) // cheapest first; unpriced last
        if (!cancelled) {
          setVariants(list)
          setVariantError(false)
          // Keep the deck's stored price for the current printing in sync with Scryfall's live value,
          // so the deck tile and this art picker never show two prices for the same printing.
          let mine = artCard.card.scryfallId && list.find(v => v.id === artCard.card.scryfallId)
          if (mine && mine.prices) {
            let nm = artCard.card.name
            let stamp = new Date().toISOString()
            let apply = x => (x.name === nm ? {...x, prices: mine.prices, pricedAt: stamp} : x)
            setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, stacks: d.stacks.map(st => ({...st, cards: st.cards.map(apply)})), commander: d.commander ? apply(d.commander) : d.commander} : d)))
          }
        }
      } catch (e) {
        if (!cancelled) setVariantError(true)
      } finally {
        if (!cancelled) setLoadingVariants(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [artCard, retryTick])

  // Apply a chosen printing's art to the clicked deck card.
  let startEditName = () => {
    if (!activeDeck) return
    setNameInput(activeDeck.name || '')
    setEditingName(true)
  }
  let saveName = () => {
    if (activeDeck) {
      let nm = nameInput.trim() || activeDeck.name
      setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, name: nm} : d)))
    }
    setEditingName(false)
  }

  // Apply a chosen printing's art to every copy of that card in the deck.
  let chooseVariant = variant => {
    if (!artCard) return
    let nm = artCard.card.name
    setDecks(ds =>
      ds.map(d =>
        d.id === activeDeckId
          ? {...d, stacks: d.stacks.map(st => ({...st, cards: st.cards.map(x => (x.name === nm ? {...x, url: variant.image_uri, scryfallUrl: variant.image_uri, scryfallId: variant.id, setCode: variant.set, setName: variant.set_name, collector_number: variant.collector_number, prices: variant.prices, pricedAt: new Date().toISOString()} : x))}))}
          : d,
      ),
    )
    setArtCard(null)
  }

  // The columns reflow: fit as many per row as keep a comfortable card size, wrapping the rest
  // below as the rail narrows. Card width then fills that row edge-to-edge (panel has 14px padding
  // each side; GAP between columns). Height/name-sliver keep their original ratios.
  const IDEAL_COL_W = deckColW
  const perRowFit = Math.max(1, Math.floor((innerW - 16 + GAP) / (IDEAL_COL_W + GAP)))
  const railCols = Math.max(1, Math.min(rails.length, perRowFit)) // side-by-side rails at this width
  const CARD_W = Math.max(96, Math.floor((innerW - 16 - GAP * (railCols - 1)) / railCols))
  const CARD_H = Math.round((CARD_W * 680) / 488)
  const VISIBLE = Math.round((CARD_H * 18) / 167) // sliver of each stacked card left showing (name strip)

  // Lay the rails out: drop each rail into the currently-shortest super-rail, so when the user's
  // arrangement is wider than fits, the extras tuck under the shortest. When it all fits, each rail
  // lands in its own super-rail in order (side by side). Measured from real rail heights, so it
  // recomputes when the layout / card counts / per-row fit / card size change (not on hover).
  let railSig = rails.map(r => r.map(k => (groupedColumns[catIndexByKey(k)] || []).length).join(':')).join('|') + '#' + rails.map(r => r.join(',')).join('|')
  useEffect(() => {
    if (!flat.length) {
      setColPos([])
      setMasonryH(0)
      return
    }
    let raf = requestAnimationFrame(() => {
      let heights = new Array(railCols).fill(0)
      let pos = []
      let srOf = []
      rails.forEach((rail, ri) => {
        let el = railRefs.current[rail[0]]
        let h = el ? el.offsetHeight : CARD_H
        let sr = heights.indexOf(Math.min(...heights))
        pos[ri] = {x: sr * (CARD_W + GAP), y: heights[sr]}
        srOf[ri] = sr
        heights[sr] += h + GAP
      })
      let total = Math.max(...heights, CARD_H)
      // The last rail in each super-rail column owns the empty space beneath it (down to the deck's
      // full height) as a drop zone, so dropping in the blank area files into that column.
      let lastInSr = {}
      rails.forEach((_, ri) => (lastInSr[srOf[ri]] = ri))
      pos.forEach((p, ri) => (p.availH = lastInSr[srOf[ri]] === ri ? total - p.y : null))
      setColPos(pos)
      setMasonryH(total)
    })
    return () => cancelAnimationFrame(raf)
  }, [railSig, railCols, CARD_W, CARD_H, sortMode])

  // Resolve a dragged tile's deckCardId back to its card (for the drag overlay).
  let findCardById = id => {
    if (!id || !activeDeck) return null
    for (let st of activeDeck.stacks) {
      let hit = st.cards.find(x => x.deckCardId === id)
      if (hit) return hit
    }
    return null
  }
  let dragCard = findCardById(activeDragId)
  let draggingCat = String(activeDragId || '').startsWith('cat-')
  let draggedCat = draggingCat ? cats.find(x => x.key === String(activeDragId).slice(4)) : null

  // Drop handling. A category handle drag (id "cat-<key>") stacks that category under another
  // (dropped on a column) or pulls it into its own rail (dropped on the "new column" strip). A
  // tile drag (id = deckCardId) pins/refiles every copy of that card into the dropped-on column.
  let onDeckDragEnd = ({active, over}) => {
    setActiveDragId(null)
    if (!over) return
    let activeId = String(active.id)
    let overId = String(over.id)
    if (activeId.startsWith('cat-')) {
      let dragKey = activeId.slice(4)
      if (overId === 'newcol') moveCatToOwnRail(dragKey)
      else if (overId.startsWith('gap-')) insertCatAtGap(dragKey, overId.slice(4) || null)
      else if (overId.startsWith('railtail-')) appendToRail(dragKey, overId.slice(9))
      else if (overId.startsWith('col-')) {
        let targetCat = cats[parseInt(overId.slice(4), 10)]
        if (targetCat) stackCatUnder(dragKey, targetCat.key)
      }
      return
    }
    let card = findCardById(activeId)
    if (!card) return
    if (overId === 'newcat') {
      // Drop a card on "+ category" → make a new category with that card pinned as its first.
      pushUndo()
      let key = `custom_${uid()}`
      let next = [...cats, {key, label: ''}]
      setDecks(ds => ds.map(d => (d.id === activeDeckId ? {...d, cats: next, stacks: d.stacks.map(st => ({...st, cards: st.cards.map(x => (x.name === card.name ? {...x, col: next.length - 1} : x))}))} : d)))
      setFocusColKey(key)
      return
    }
    if (overId.startsWith('col-')) moveCardToColumn(card.name, parseInt(overId.slice(4), 10))
  }

  // One tile per unique card, with a count badge and +/- to change copies.
  // Commit a deferred delete once the user stops hovering/expanding the placeholder (see − below).
  useEffect(() => {
    if (ghost && expanded !== ghost.id) {
      removeOne(ghost.stackId, ghost.id)
      setGhost(null)
    }
  }, [expanded, ghost])

  let renderTile = (group, i, col) => {
    let {card, stackId, ids} = group
    let count = ids.length
    let isPinned = artCard && artCard.deckCardId === card.deckCardId // art picker open for this card → stay enlarged + highlighted
    let isExpanded = expanded === card.deckCardId || isPinned
    let isGhost = ghost && ghost.id === card.deckCardId
    let isJustAdded = !isGhost && justAddedName != null && card.name === justAddedName
    let hoverMode = expandMode !== 'click'
    let prevId = i > 0 ? col[i - 1].card.deckCardId : null
    let prevExpanded = i > 0 && (expanded === prevId || (artCard && artCard.deckCardId === prevId))
    let marginTop = i === 0 ? 0 : prevExpanded ? 8 : -(CARD_H - VISIBLE)
    return (
      <Draggable key={card.deckCardId} id={card.deckCardId}>
        {({attributes, listeners, setNodeRef, isDragging}) => (
          <div
            ref={setNodeRef}
            {...(readOnly ? {} : attributes)}
            {...(readOnly ? {} : listeners)}
            onMouseEnter={hoverMode ? () => setExpanded(card.deckCardId) : undefined}
            onMouseLeave={hoverMode ? () => setExpanded(null) : undefined}
            onClick={() => {
              setJustAddedName(null) // any tile click clears the fresh-add gold border
              if (isGhost) { setExpanded(null); return }
              if (!readOnly && hoverMode) setArtCard({stackId, deckCardId: card.deckCardId, card})
              else setExpanded(e => (e === card.deckCardId ? null : card.deckCardId))
            }}
            style={{position: 'relative', marginTop, zIndex: isExpanded ? 10 : 1, transition: 'margin-top 0.2s ease', width: CARD_W, cursor: readOnly ? 'pointer' : (hoverMode ? 'grab' : 'pointer'), opacity: isDragging ? 0.3 : 1, touchAction: 'none'}}>
            {/* On delete, the art fades to near-nothing over white (a held placeholder) until unhovered. */}
            <img
              src={card.url}
              alt={card.name}
              onError={e => onImgError(e, card)}
              draggable={false}
              style={{width: CARD_W, height: CARD_H, borderRadius: 8, display: 'block', opacity: isGhost ? 0.1 : 1, transition: 'opacity 0.35s ease', boxShadow: isGhost ? 'none' : isJustAdded ? '0 0 10px rgba(240,193,75,0.6), 0 2px 8px rgba(0,0,0,0.4)' : isExpanded ? '0 6px 20px rgba(0,0,0,0.6)' : '0 2px 8px rgba(0,0,0,0.35)', outline: isGhost ? 'none' : isJustAdded ? '2px solid #f0c14b' : isPinned ? `2px solid ${c.accent}` : 'none'}}
            />
            {!isGhost && count > 1 && <div style={s.countBadge}>{count}</div>}
            {!isGhost && isExpanded && (
              <div style={s.cardBottom} onClick={e => e.stopPropagation()}>
                <div style={s.priceTag}>
                  TCG <span style={{color: card.prices?.usd ? '#4ade80' : c.textDim}}>{card.prices?.usd ? `$${card.prices.usd}` : '--'}</span>
                  {card.prices?.eur ? <span> · CM <span style={{color: c.text}}>€{card.prices.eur}</span></span> : null}
                </div>
                {!readOnly && (
                <div style={s.cardControls}>
                  <button style={s.stepBtn} title="Remove one" onClick={e => { e.stopPropagation(); if (count === 1) setGhost({stackId, id: ids[0]}); else removeOne(stackId, ids[ids.length - 1]) }}>
                    −
                  </button>
                  <span style={s.count}>{count}</span>
                  <button style={s.stepBtn} title="Add one" onClick={e => { e.stopPropagation(); addCopy(stackId, card) }}>
                    +
                  </button>
                  {!hoverMode && (
                    <button style={s.stepBtn} title="Change art" onClick={e => { e.stopPropagation(); setArtCard({stackId, deckCardId: card.deckCardId, card}) }}>
                      🎨
                    </button>
                  )}
                </div>
                )}
              </div>
            )}
          </div>
        )}
      </Draggable>
    )
  }

  // --- Admin-gated AI suggestions (server proxies OpenAI; mirrors the main deck builder) ---
  // When an admin opens the panel, check once whether the server has an OpenAI key configured.
  useEffect(() => {
    if (!showAi || !isAdmin || providerKeys !== null) return
    let dead = false
    fetch(`${getBackendUrl()}/api/get-api-key`, {credentials: 'include'})
      .then(r => r.json())
      .then(d => {
        if (dead) return
        let pk = {}
        AI_PROVIDER_LIST.forEach(p => { pk[p] = !!(d.providers && d.providers[p] && d.providers[p].hasKey) })
        setProviderKeys(pk)
        if (d.provider) setAiProvider(d.provider)
      })
      .catch(() => { if (!dead) setProviderKeys({}) })
    return () => { dead = true }
  }, [showAi, isAdmin, providerKeys])
  useEffect(() => { try { localStorage.setItem('blogAiModel', aiModel) } catch (e) {} }, [aiModel])
  useEffect(() => { try { localStorage.setItem('blogAiCardW', String(aiCardW)) } catch (e) {} }, [aiCardW])
  useEffect(() => { try { localStorage.setItem('blogShowAi', showAi ? '1' : '0') } catch (e) {} }, [showAi])
  // Remember the last AI prompt + results per deck so a refresh (with the panel open) restores them.
  // Save runs BEFORE restore so a deck switch (ref still on the old deck) skips saving stale data.
  let aiDeckRef = useRef(null)
  useEffect(() => {
    if (!activeDeckId || aiDeckRef.current !== activeDeckId) return
    try { localStorage.setItem(`blogAiResults_${activeDeckId}`, JSON.stringify({question: aiQuestion, cards: aiCards})) } catch (e) {}
  }, [aiQuestion, aiCards, activeDeckId])
  useEffect(() => {
    aiDeckRef.current = activeDeckId
    if (!activeDeckId) { setAiQuestion(''); setAiCards([]); return }
    try {
      let raw = localStorage.getItem(`blogAiResults_${activeDeckId}`)
      let d = raw ? JSON.parse(raw) : {}
      setAiQuestion(d.question || '')
      setAiCards(Array.isArray(d.cards) ? d.cards : [])
    } catch (e) { setAiQuestion(''); setAiCards([]) }
  }, [activeDeckId])
  // The model actually sent: the admin's pick if it's valid for the current provider, else the cheap default.
  let effectiveAiModel = () => {
    let opts = MODELS_BY_PROVIDER[aiProvider] || OPENAI_MODELS
    return aiModel && opts.some(m => m[0] === aiModel) ? aiModel : AI_MODEL_DEFAULT[aiProvider]
  }
  let providerHasKey = p => !!(providerKeys && providerKeys[p])
  let submitAdminPass = async () => {
    if (!adminPass.trim() || adminChecking) return
    setAdminChecking(true); setAdminError(false)
    try {
      let res = await fetch(`${getBackendUrl()}/admin/login`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({password: adminPass})})
      if (!res.ok) { setAdminError(true); return }
      let data = await res.json()
      try { localStorage.setItem('adminToken', data.token) } catch (e) {}
      setIsAdmin(true); setAdminPass('')
    } catch (e) { setAdminError(true) } finally { setAdminChecking(false) }
  }
  let saveAiApiKey = async () => {
    if (apiKeySaving) return
    // Empty field but this provider already has a saved key → just use the existing one.
    if (!apiKeyInput.trim()) {
      if (providerHasKey(aiProvider)) { setEditKey(false); setKeyError(null) }
      return
    }
    setApiKeySaving(true); setKeyError(null)
    try {
      let res = await fetch(`${getBackendUrl()}/api/save-api-key`, {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({apiKey: apiKeyInput, provider: aiProvider})})
      let data = await res.json().catch(() => ({}))
      if (res.ok && data.success !== false) { setProviderKeys(pk => ({...(pk || {}), [aiProvider]: true})); setApiKeyInput(''); setEditKey(false) }
      else setKeyError(data.error || 'Could not verify the key.')
    } catch (e) { setKeyError('Save failed — is the server running?') } finally { setApiKeySaving(false) }
  }
  let resolveAiCards = async list => {
    setAiCardsLoading(true); setAiCards([])
    let out = []
    for (let card of list) {
      try {
        let res = await fetch(`${getBackendUrl()}/api/card-lookup?name=${encodeURIComponent(card.name)}`)
        let data = await res.json()
        if (data.success && data.card) out.push({...card, name: data.card.name, image: data.card.image_uri, sc: data.card})
        else out.push({...card, image: null})
      } catch (e) { out.push({...card, image: null}) }
      setAiCards([...out])
      await new Promise(r => setTimeout(r, 50))
    }
    setAiCardsLoading(false)
  }
  // A short two-note chime when a long AI run finishes (so you can look away). Unlocked on the submit
  // gesture (browsers require a user gesture before audio); no asset — synthesized via Web Audio.
  let ensureAudio = () => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)()
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume()
    } catch (e) {}
  }
  let playDing = () => {
    try {
      let ctx = audioCtxRef.current
      if (!ctx) return
      let now = ctx.currentTime
      ;[880, 1320].forEach((freq, i) => {
        let osc = ctx.createOscillator()
        let gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        let t = now + i * 0.13
        gain.gain.setValueAtTime(0.0001, t)
        gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5)
        osc.connect(gain).connect(ctx.destination)
        osc.start(t)
        osc.stop(t + 0.55)
      })
    } catch (e) {}
  }
  let submitAiQuestion = async () => {
    if (!aiQuestion.trim() || aiAsking || !activeDeck) return
    setAiAsking(true); setAiError(null); setAiCards([])
    let startedAt = Date.now()
    ensureAudio() // unlock the chime within this click/Enter gesture
    let controller = new AbortController()
    aiAbortRef.current = controller
    try {
      let names = []
      activeDeck.stacks.forEach(st => st.cards.forEach(cd => names.push(cd.name)))
      let commander = activeDeck.commander?.name || ''
      let colorIdentity = (activeDeck.commander?.color_identity || []).join('')
      let res = await fetch(`${getBackendUrl()}/api/ai-question`, {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({question: aiQuestion, deckList: names.join('\n'), commander, colorIdentity, reasoningEffort: aiEffort, model: effectiveAiModel(), provider: aiProvider}), signal: controller.signal})
      let data = await res.json()
      if (!data.success) { setAiError(data.error || 'Something went wrong.'); return }
      if (data.cards && data.cards.length) {
        // Full-deck mode: import straight into an empty/commander-only deck; otherwise show suggestions.
        let nonCmdr = names.filter(n => n.toLowerCase() !== (commander || '').toLowerCase()).length
        if (data.mode === 'deck' && nonCmdr === 0) importAiDeck(data.cards)
        else resolveAiCards(data.cards)
      } else setAiError('No suggestions came back — try rephrasing.')
    } catch (e) { if (e.name !== 'AbortError') setAiError('Request failed.') } finally { setAiAsking(false); aiAbortRef.current = null; if (!controller.signal.aborted && Date.now() - startedAt > 6000) playDing() }
  }
  // Stop button — cancel the in-flight AI request.
  let stopAiQuestion = () => {
    if (aiAbortRef.current) aiAbortRef.current.abort()
    setAiAsking(false)
    setAiCardsLoading(false)
  }
  // Full-deck mode: resolve every card the model returned (server-cached import resolver) and drop them
  // into the active deck in ONE undoable action, keeping its commander. Never re-adds the commander.
  let importAiDeck = async aiCards => {
    if (!activeDeck) return
    let cmdr = (activeDeck.commander?.name || '').toLowerCase()
    let qtyByName = new Map()
    aiCards.forEach(c => {
      let n = (c.name || '').trim()
      if (!n || n.toLowerCase() === cmdr) return
      qtyByName.set(n, Math.min((qtyByName.get(n) || 0) + (parseInt(c.quantity, 10) || 1), 99))
    })
    let names = [...qtyByName.keys()]
    if (!names.length) return
    pushUndo()
    setAiBuilding({current: 0, total: names.length})
    let backend = getBackendUrl()
    let resolved = new Map()
    for (let i = 0; i < names.length; i += 4) {
      let batch = names.slice(i, i + 4)
      let cards = await Promise.all(batch.map(n => resolveCard(backend, n)))
      batch.forEach((n, j) => cards[j] && resolved.set(n, cards[j]))
      setAiBuilding({current: Math.min(i + 4, names.length), total: names.length})
      await new Promise(r => setTimeout(r, 350))
    }
    for (let n of [...resolved.keys()]) resolved.set(n, await cacheScryfallCard(backend, resolved.get(n)))
    let found = []
    for (let [n, qty] of qtyByName) {
      let card = resolved.get(n)
      if (!card) continue
      for (let k = 0; k < qty; k++) found.push({...card, deckCardId: uid()})
    }
    let stackId = activeDeck.stacks[0]?.id
    if (stackId && found.length) { mutateStack(stackId, cards => [...cards, ...found]); autoOptimizeRef.current = true }
    setAiBuilding(null)
    let missing = names.filter(n => !resolved.has(n))
    if (missing.length) setAiError(`Built the deck — couldn't find ${missing.length}: ${missing.slice(0, 6).join(', ')}${missing.length > 6 ? '…' : ''}`)
  }
  let addAiCard = card => {
    let sc = card.sc
    if (!sc) return
    addCardToDeck({name: sc.name, url: sc.image_uri, scryfallId: sc.scryfallId, setCode: sc.set, setName: sc.set_name, collector_number: sc.collector_number, colors: sc.colors || [], color_identity: sc.color_identity || [], type_line: sc.type_line || '', cmc: sc.cmc || 0, mana_cost: sc.mana_cost || '', prices: sc.prices || null})
  }

  // Commander art thumbnail for a deck (for the deck-switcher list). Falls back to the first card.
  let deckArt = deck => {
    let cm = deck?.commander
    if (cm?.scryfallId) return `https://api.scryfall.com/cards/${cm.scryfallId}?format=image&version=art_crop`
    if (cm?.url) return cm.url
    return deck?.stacks?.[0]?.cards?.[0]?.url || null
  }
  let deckCardCount = deck => deck?.stacks?.reduce((n, st) => n + st.cards.length, 0) || 0
  let deckPriceOf = deck => (deck?.stacks || []).reduce((sum, st) => sum + st.cards.reduce((s2, cd) => s2 + (parseFloat(cd.prices?.usd) || 0), 0), 0)

  // Deck stats (type spread, mana curve, color-pip mix) over every copy in the deck. Computed only
  // when the panel is open. Types go in a single primary bucket so the counts sum to the deck size;
  // pips come from mana_cost ("{2}{B}{B}") with a fallback to colors[] for older imports; the curve
  // counts nonland cards by rounded mana value (0..7+).
  let computeDeckStats = () => {
    let types = {Creature: 0, Instant: 0, Sorcery: 0, Artifact: 0, Enchantment: 0, Planeswalker: 0, Land: 0, Other: 0}
    let pips = {W: 0, U: 0, B: 0, R: 0, G: 0}
    let curve = [0, 0, 0, 0, 0, 0, 0, 0]
    let mdfc = 0, nonlandCount = 0, cmcSum = 0
    flat.forEach(({card}) => {
      let t = (card.type_line || '').toLowerCase()
      let isLand = t.includes('land')
      let bucket = isLand ? 'Land'
        : t.includes('creature') ? 'Creature'
        : t.includes('planeswalker') ? 'Planeswalker'
        : t.includes('instant') ? 'Instant'
        : t.includes('sorcery') ? 'Sorcery'
        : t.includes('artifact') ? 'Artifact'
        : t.includes('enchantment') ? 'Enchantment'
        : 'Other'
      types[bucket]++
      if (t.includes('//')) mdfc++
      let mc = card.mana_cost || ''
      if (mc) {
        ;(mc.match(/\{[^}]+\}/g) || []).forEach(sym => sym.slice(1, -1).split('/').forEach(p => { if (pips[p] != null) pips[p]++ }))
      } else {
        ;(card.colors || []).forEach(col => { if (pips[col] != null) pips[col]++ })
      }
      if (!isLand) {
        curve[Math.min(Math.round(card.cmc || 0), 7)]++
        nonlandCount++
        cmcSum += card.cmc || 0
      }
    })
    let pipTotal = pips.W + pips.U + pips.B + pips.R + pips.G
    return {types, pips, pipTotal, curve, curveMax: Math.max(1, ...curve), mdfc, avgCmc: nonlandCount ? cmcSum / nonlandCount : 0, total: flat.length}
  }

  // Wide deck editor → merge the controls row up onto the header row (3 top rows collapse to 2).
  const wideEditor = innerW > 800
  // Sort / Optimize / Export / Import — rendered either as their own row (narrow) or on the header row (wide).
  let controlButtons = (
    <>
      {wideEditor && activeDeck && flat.length > 0 && (
        <button className="blueHover" style={{...s.subtleBtn, ...(showStats ? {borderColor: c.accent, color: c.accent} : {})}} onClick={() => setShowStats(v => !v)} title="Show deck stats">
          Stats
        </button>
      )}
      {activeDeck && flat.length > 0 && (
        <button className="blueHover" style={s.subtleBtn} onClick={() => setSortMode(m => (m === 'price' ? 'cmc' : 'price'))} title="Toggle column sort between mana value and price">
          Sort: {sortMode === 'price' ? 'Price' : 'CMC'}
        </button>
      )}
      {activeDeck && flat.length > 0 && (
        <button
          className="blueHover"
          style={{...s.subtleBtn, display: 'inline-flex', alignItems: 'center', gap: 6, ...(optimizing ? {color: '#4ade80', cursor: 'default'} : {})}}
          onClick={optimizeDeckPrices}
          disabled={optimizing}
          title="Swap every card to its cheapest printing">
          {optimizing ? (
            <>
              <span style={s.optTrack}>
                <span style={{...s.optFill, width: `${optimizeProgress.total ? (optimizeProgress.current / optimizeProgress.total) * 100 : 0}%`}} />
              </span>
              <span style={{fontSize: 11}}>
                {optimizeProgress.current === optimizeProgress.total && optimizeProgress.saved > 0 ? `-$${optimizeProgress.saved.toFixed(2)}` : `${optimizeProgress.current}/${optimizeProgress.total}`}
              </span>
            </>
          ) : (
            'Optimize'
          )}
        </button>
      )}
      {activeDeck && flat.length > 0 && (
        <button className="blueHover" style={s.subtleBtn} onClick={exportDeck} title="Copy decklist to clipboard">
          {exported ? 'Copied' : 'Export'}
        </button>
      )}
      {(activeDeck || decks.length > 0) && (
        <button className="blueHover" style={s.subtleBtn} title="Import a decklist" onClick={() => setShowImport(v => !v)}>
          {showImport ? 'Close' : 'Import'}
        </button>
      )}
    </>
  )

  // Enriched art picker: the currently-applied printing (match by scryfallId, else cheapest) drives the detail header.
  const artDetail = artCard ? (variants.find(v => v.id === artCard.card.scryfallId) || variants[0] || null) : null

  return (
    <>
    <div ref={panelRef} style={s.panel}>
      <DndContext sensors={dndSensors} collisionDetection={catAwareCollision} onDragStart={({active}) => setActiveDragId(active.id)} onDragEnd={onDeckDragEnd} onDragCancel={() => setActiveDragId(null)}>
      <div style={s.topRow}>
        {portraitSrc && (
          <img
            src={portraitSrc}
            alt="commander"
            draggable={!readOnly && !!activeDeck}
            onDragStart={!readOnly && activeDeck ? (e => {
              let payload = JSON.stringify({deckId: activeDeck.id, name: activeDeck.name || activeDeck.commander?.name || 'Deck', art: portraitSrc})
              e.dataTransfer.setData('application/x-moxdeck', payload)
              e.dataTransfer.setData('text/plain', activeDeck.name || 'Deck')
              e.dataTransfer.effectAllowed = 'copy'
            }) : undefined}
            onError={e => {
              if (portraitCard?.url && e.target.src !== portraitCard.url) e.target.src = portraitCard.url
            }}
            title={!readOnly && activeDeck ? 'Drag into the tier list to add this deck' : undefined}
            style={!readOnly && activeDeck ? {...s.portrait, cursor: 'grab'} : s.portrait}
          />
        )}
        <div style={s.topRight}>
      <div style={s.header}>
        <span style={s.titleWrap}>
          {activeDeck ? (
            editingName ? (
              <input
                style={s.titleEdit}
                value={nameInput}
                autoFocus
                placeholder={activeDeck.name}
                onChange={e => setNameInput(e.target.value)}
                onBlur={saveName}
                onFocus={e => e.target.select()}
                onKeyDown={e => {
                  if (e.key === 'Enter') e.target.blur()
                  else if (e.key === 'Escape') setEditingName(false)
                }}
              />
            ) : (
              <span
                style={readOnly ? {...s.titleName, cursor: 'default', borderBottomColor: 'transparent'} : s.titleName}
                title={readOnly ? undefined : 'Click to rename this deck'}
                onClick={readOnly ? undefined : startEditName}
                onMouseOver={readOnly ? undefined : e => (e.currentTarget.style.borderBottomColor = c.textDim)}
                onMouseOut={readOnly ? undefined : e => (e.currentTarget.style.borderBottomColor = 'transparent')}>
                {activeDeck.name}
              </span>
            )
          ) : (
            <span style={s.title}>Deck</span>
          )}
          {activeDeck && <span style={s.deckCount}>{flat.length} cards</span>}
          {activeDeck && <span style={s.deckTotal}>${deckTotal.toFixed(2)}</span>}
        </span>
        {!readOnly && (
        <span style={{display: 'flex', alignItems: 'center', gap: 8, position: 'relative'}}>
          {wideEditor && controlButtons}
          <button className="blueHover" style={s.headerIconBtn} title="Settings" onClick={() => setShowSettings(v => !v)}>⚙</button>
          {showSettings && (
            <>
              <div style={{position: 'fixed', inset: 0, zIndex: 40}} onClick={() => setShowSettings(false)} />
              <div style={s.settingsPop}>
                <div style={{fontSize: 11, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8}}>Settings</div>
                <label style={s.settingRow}>
                  <input type="checkbox" checked={expandMode === 'click'} onChange={e => setExpandMode(e.target.checked ? 'click' : 'hover')} style={{marginTop: 2, flexShrink: 0}} />
                  <span>
                    Click to expand cards
                    <span style={{display: 'block', color: c.textDim, fontSize: 10, marginTop: 2}}>Tap a card to expand/collapse instead of hovering — for browsers that handle hover poorly.</span>
                  </span>
                </label>
              </div>
            </>
          )}
          {(currentUser || decks.length > 0) && (
            <button className="blueHover" style={s.headerIconBtn} title="My decks" onClick={() => setShowDeckList(true)}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
              </svg>
            </button>
          )}
        </span>
        )}
      </div>

      {/* Logged-out: minimal login so the blog can save under your user */}
      {!readOnly && authChecked && !currentUser && (
        <form onSubmit={handleAuth} style={s.authBox}>
          <div style={{color: c.textDim, fontSize: 12, marginBottom: 6}}>Log in to save your deck to your account.</div>
          <input style={s.input} placeholder="username" value={authUser} onChange={e => setAuthUser(e.target.value)} />
          <input style={s.input} type="password" placeholder="password" value={authPass} onChange={e => setAuthPass(e.target.value)} />
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <button type="submit" style={s.primaryBtn}>
              {authMode === 'login' ? 'Log in' : 'Register'}
            </button>
            <button type="button" style={s.linkBtn} onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}>
              {authMode === 'login' ? 'need an account?' : 'have an account?'}
            </button>
          </div>
          {authError && <div style={{color: c.danger, fontSize: 12, marginTop: 6}}>{authError}</div>}
        </form>
      )}

      {/* Deck controls — their own row when narrow; merged up onto the header row when the editor is wide. */}
      {!readOnly && !wideEditor && <div style={s.controls}>{controlButtons}</div>}

      {/* Bottom half of the header: search to add cards straight into the deck */}
      {!readOnly && activeDeck && (
        <div style={s.addSearch}>
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            {!wideEditor && flat.length > 0 && (
              <button
                className="blueHover"
                style={{...s.subtleBtn, flexShrink: 0, ...(showStats ? {borderColor: c.accent, color: c.accent} : {})}}
                onClick={() => setShowStats(v => !v)}
                title="Show deck stats">
                Stats
              </button>
            )}
            <input style={{...s.addInput, flex: 1, minWidth: 0}} value={addQuery} placeholder="🔍 sol ring" name="mtg-card-search" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} onChange={e => setAddQuery(e.target.value)} />
            <Droppable id="newcat">
              {({setNodeRef, isOver}) => (
                <button
                  ref={setNodeRef}
                  className="blueHover"
                  style={{...s.subtleBtn, flexShrink: 0, whiteSpace: 'nowrap', ...(dragCard ? {border: `2px dashed ${isOver ? c.accent : 'rgba(77,163,255,0.55)'}`, background: isOver ? 'rgba(77,163,255,0.14)' : undefined, color: c.accent} : {})}}
                  onClick={addCategory}
                  title={dragCard ? 'Drop a card here to start a new category with it' : 'Add a custom column'}>
                  + category
                </button>
              )}
            </Droppable>
            {/* Zoom: adjust the card size for the whole deck (columns wrap to fit) */}
            <button
              className="blueHover"
              style={{...s.subtleBtn, flexShrink: 0, padding: '6px 9px', fontSize: 15, lineHeight: 1, ...(deckColW <= 70 ? {opacity: 0.4, cursor: 'default'} : {})}}
              onClick={() => setDeckColW(w => Math.max(70, w - 15))}
              disabled={deckColW <= 70}
              title="Smaller cards">
              −
            </button>
            <button
              className="blueHover"
              style={{...s.subtleBtn, flexShrink: 0, padding: '6px 9px', fontSize: 15, lineHeight: 1, ...(deckColW >= 220 ? {opacity: 0.4, cursor: 'default'} : {})}}
              onClick={() => setDeckColW(w => Math.min(220, w + 15))}
              disabled={deckColW >= 220}
              title="Bigger cards">
              +
            </button>
            <button
              className="blueHover"
              style={{...s.subtleBtn, flexShrink: 0, padding: '6px 9px', opacity: undoStack.length ? 1 : 0.4, cursor: undoStack.length ? 'pointer' : 'default'}}
              onClick={() => { if (!undoStack.length) return; setUndoSpin(n => n - 1); undo() }}
              disabled={!undoStack.length}
              title={undoStack.length ? 'Undo last change' : 'Nothing to undo'}>
              <span style={{display: 'inline-block', transform: `rotate(${undoSpin * 360}deg)`, transition: 'transform 0.4s ease'}}>↺</span>
            </button>
            <button
              className="blueHover"
              style={{...s.subtleBtn, flexShrink: 0, padding: '6px 9px', fontSize: 15, ...(showAi ? {borderColor: c.accent} : {})}}
              onClick={() => setShowAi(v => !v)}
              title="AI card suggestions">
              ✨
            </button>
          </div>
          {(addResults.length > 0 || (addSearching && addQuery.trim())) && (
            <div className="thinScroll" style={s.addResults}>
              {addSearching && addResults.length === 0 && <div style={s.addResultEmpty}>Searching…</div>}
              {addResults.map((card, i) => (
                <div
                  key={card.scryfallId || `${card.name}_${i}`}
                  style={s.addResult}
                  onClick={() => addCardToDeck(card)}
                  onMouseOver={e => (e.currentTarget.style.background = c.panel2)}
                  onMouseOut={e => (e.currentTarget.style.background = 'transparent')}>
                  {card.url && <img src={card.url} alt="" onError={e => onImgError(e, card)} style={s.addThumb} />}
                  <div style={s.addResultText}>
                    <div style={s.addResultName}>{card.name}</div>
                    <div style={s.addResultSet}>
                      {card.setName || card.setCode || ''}
                      {card.prices?.usd ? ` · $${card.prices.usd}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
        </div>
      </div>

      {/* Admin-only AI card suggestions — opened by the ✨ button in the search row. Passcode-gated. */}
      {!readOnly && activeDeck && showAi && (
        <div style={s.aiWrap}>
            <div style={s.aiPanel}>
              {!isAdmin ? (
                <div>
                  <div style={{color: c.textDim, fontSize: 12, marginBottom: 8}}>Admin only for now — enter the passcode to unlock AI suggestions.</div>
                  <div style={{display: 'flex', gap: 8}}>
                    <input type="password" value={adminPass} placeholder="Admin passcode" autoFocus
                      name="admin-gate" autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                      onChange={e => { setAdminPass(e.target.value); setAdminError(false) }}
                      onKeyDown={e => { if (e.key === 'Enter') submitAdminPass() }}
                      style={{...s.addInput, flex: 1, ...(adminError ? {borderColor: c.danger} : {})}} />
                    <button style={s.subtleBtn} onClick={submitAdminPass} disabled={adminChecking || !adminPass.trim()}>{adminChecking ? '…' : 'Unlock'}</button>
                  </div>
                  {adminError && <div style={{color: c.danger, fontSize: 11, marginTop: 6}}>Incorrect passcode.</div>}
                </div>
              ) : providerKeys === null ? (
                <div style={{color: c.textDim, fontSize: 12}}>Checking API key…</div>
              ) : (!providerHasKey(aiProvider) || editKey) ? (
                <div>
                  <div style={{display: 'flex', gap: 6, marginBottom: 8}}>
                    {[['openai', 'OpenAI'], ['anthropic', 'Claude'], ['deepseek', 'DeepSeek']].map(([p, label]) => (
                      <button key={p} onClick={() => { setAiProvider(p); setKeyError(null) }} style={{...s.subtleBtn, padding: '5px 10px', fontSize: 12, ...(aiProvider === p ? {borderColor: c.accent, color: c.accent} : {})}}>{label}{providerHasKey(p) ? ' ✓' : ''}</button>
                    ))}
                  </div>
                  <div style={{display: 'flex', gap: 8}}>
                    <input type="password" value={apiKeyInput}
                      placeholder={providerHasKey(aiProvider) ? 'key on file — paste to replace' : aiProvider === 'anthropic' ? 'sk-ant-…' : 'sk-…'}
                      name="ai-provider-key" autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                      onChange={e => { setApiKeyInput(e.target.value); setKeyError(null) }}
                      onKeyDown={e => { if (e.key === 'Enter') saveAiApiKey() }}
                      style={{...s.addInput, flex: 1}} />
                    <button style={s.subtleBtn} onClick={saveAiApiKey} disabled={apiKeySaving || (!apiKeyInput.trim() && !providerHasKey(aiProvider))}>{apiKeySaving ? 'Checking…' : !apiKeyInput.trim() && providerHasKey(aiProvider) ? 'Use saved' : 'Save'}</button>
                  </div>
                  {keyError && <div style={{color: c.danger, fontSize: 11, marginTop: 6}}>{keyError}</div>}
                  <div style={{color: c.textDim, fontSize: 11, marginTop: 8, lineHeight: 1.5}}>
                    {providerHasKey(aiProvider) && <span style={{color: '#a7e0b5'}}>✓ A key for this provider is saved — press "Use saved" to keep it, or paste a new one to replace. </span>}
                    🔒 Stored encrypted on the server, used only server-side — never exposed to the browser or other users.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{display: 'flex', gap: 8, alignItems: 'stretch'}}>
                    <textarea value={aiQuestion} placeholder={'Ask for cards to add — e.g. "more card draw" or "cheap removal under $3"…'} rows={2}
                      onChange={e => setAiQuestion(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitAiQuestion() } }}
                      style={{...s.addInput, flex: 1, resize: 'vertical', fontFamily: 'inherit'}} />
                    <div style={{display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'space-between'}}>
                      <div style={{display: 'flex', gap: 6, width: 165}}>
                        <select value={effectiveAiModel()} onChange={e => setAiModel(e.target.value)} style={{...s.aiSelect, flex: 1, minWidth: 0}} title="Model (admin — applies to everyone)">
                          {(MODELS_BY_PROVIDER[aiProvider] || OPENAI_MODELS).map(([id, label]) => (
                            <option key={id} value={id}>{label}</option>
                          ))}
                        </select>
                        <button style={{...s.aiSelect, width: 32, flexShrink: 0, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14}} onClick={() => setEditKey(true)} title="AI settings — key / provider">⚙</button>
                      </div>
                      {(aiProvider === 'openai' || aiProvider === 'anthropic') && (
                        <select value={aiEffort} onChange={e => setAiEffort(e.target.value)} style={s.aiSelect} title="Thinking / reasoning effort (thinking + reasoning models)">
                          <option value="low">Fast</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="xhigh">xHigh</option>
                          <option value="max">Max</option>
                        </select>
                      )}
                      <div style={{display: 'flex', gap: 6}}>
                        <button
                          style={{...s.subtleBtn, flex: 1, minWidth: 0, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 32, transition: 'color 0.2s ease, border-color 0.2s ease'}}
                          onClick={submitAiQuestion}
                          disabled={aiAsking || !aiQuestion.trim()}
                          onMouseOver={e => { if (!aiAsking) { e.currentTarget.style.color = '#8fc8ff'; e.currentTarget.style.borderColor = '#8fc8ff' } }}
                          onMouseOut={e => { e.currentTarget.style.color = c.textDim; e.currentTarget.style.borderColor = c.panel2 }}>
                          {aiAsking ? <span className="blogSpinner" /> : 'Send'}
                        </button>
                        {aiAsking && (
                          <button
                            onClick={stopAiQuestion}
                            title="Stop"
                            style={{...s.subtleBtn, width: 60, flexShrink: 0, minHeight: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderColor: c.danger}}
                            onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,107,107,0.14)')}
                            onMouseOut={e => (e.currentTarget.style.background = 'transparent')}>
                            <span style={{width: 12, height: 12, background: c.danger, borderRadius: 2, display: 'block'}} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {aiError && <div style={{color: c.danger, fontSize: 12, marginTop: 8}}>{aiError}</div>}
                  {aiBuilding && <div style={{color: c.textDim, fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8}}><span className="blogSpinner" /> Building deck… {aiBuilding.current}/{aiBuilding.total}</div>}
                  {aiAsking && aiStage && <div style={{color: c.textDim, fontSize: 11, marginTop: 6, opacity: 0.5}}>{aiStage}</div>}
                  {(aiCards.length > 0 || aiCardsLoading) && (
                    <div style={{marginTop: 12}}>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8}}>
                        <span style={{fontSize: 11, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5}}>Suggestions {aiCards.length ? `(${aiCards.length})` : ''} — click to add</span>
                        <span style={{display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0}}>
                          <button className="blueHover" style={s.sizeBtn} onClick={() => setAiCardW(w => Math.max(90, w - 28))} title="Smaller cards">−</button>
                          <button className="blueHover" style={s.sizeBtn} onClick={() => setAiCardW(w => Math.min(280, w + 28))} title="Larger cards">+</button>
                        </span>
                      </div>
                      <div style={{...s.aiGrid, gridTemplateColumns: `repeat(auto-fill, minmax(${aiCardW}px, 1fr))`}}>
                        {aiCards.map((card, i) => (
                          <div key={card.name + i} style={s.aiCard} title={card.why || card.name}
                            onClick={() => card.sc && addAiCard(card)}
                            onMouseOver={e => (e.currentTarget.style.borderColor = c.accent)}
                            onMouseOut={e => (e.currentTarget.style.borderColor = 'transparent')}>
                            {card.image ? (
                              <div style={{position: 'relative'}}>
                                <img src={card.image} alt={card.name} style={s.aiCardImg} />
                                {card.sc?.prices?.usd && <span style={s.aiCardPrice}>${card.sc.prices.usd}</span>}
                              </div>
                            ) : <div style={s.aiCardStub}>{card.name}</div>}
                            {card.why && <div style={s.aiCardWhy}>{card.why}</div>}
                          </div>
                        ))}
                        {aiCardsLoading && <div style={{color: c.textDim, fontSize: 12, alignSelf: 'center'}}>Resolving…</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>
      )}

      {pendingCommander && (
        <div style={s.pendingBox}>
          <span style={{color: c.textDim, fontSize: 12}}>Imported "{pendingCommander.name}".</span>
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <button style={s.primaryBtn} onClick={() => { onAddCommanderTile?.(pendingCommander); setPendingCommander(null) }}>
              Add to tier list
            </button>
            <button style={s.linkBtn} onClick={() => setPendingCommander(null)}>
              dismiss
            </button>
          </div>
        </div>
      )}

      {importWarning && (
        <div style={{...s.pendingBox, border: `1px solid ${c.accent}`}}>
          <span style={{color: c.text, fontSize: 12}}>
            Couldn't find {importWarning.missing.length} card{importWarning.missing.length > 1 ? 's' : ''} — likely rate-limited. Re-paste just these lines to retry:
          </span>
          <div style={{color: c.textDim, fontSize: 11, lineHeight: 1.5, maxHeight: 120, overflowY: 'auto'}}>
            {importWarning.missing.join(', ')}
          </div>
          <div>
            <button style={s.linkBtn} onClick={() => setImportWarning(null)}>dismiss</button>
          </div>
        </div>
      )}

      {!readOnly && showImport && (
        <div style={s.importBox}>
          <textarea style={s.textarea} placeholder={'Paste a decklist, e.g.\n2 Lightning Bolt\n1 Sol Ring'} value={importText} onChange={e => setImportText(e.target.value)} disabled={importing} />
          {importing && importProgress && (
            <div style={s.progressTrack}>
              <div style={{...s.progressFill, width: `${Math.round((importProgress.current / Math.max(1, importProgress.total)) * 100)}%`}} />
            </div>
          )}
          <div style={s.importFooter}>
            {importing ? (
              <span style={{color: c.textDim, fontSize: 12}}>
                Resolving {importProgress?.current}/{importProgress?.total}...
              </span>
            ) : (
              <span style={{color: c.textDim, fontSize: 12}}>One card per line. "2 Name" for copies.</span>
            )}
            <button style={{...s.subtleBtn, ...(importing ? {opacity: 0.6, cursor: 'default'} : {})}} onClick={importDeck} disabled={importing || !importText.trim()}>
              {importing ? (
                <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                  <span className="blogSpinner" /> Importing...
                </span>
              ) : (
                'Import'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Stats row (toggled by the Stats button under the portrait): type spread, mana curve,
          color-pip mix. Slides the deck down to make room. */}
      {showStats && activeDeck && flat.length > 0 && (() => {
        let st = computeDeckStats()
        let typeRows = [['Creatures', st.types.Creature], ['Instants', st.types.Instant], ['Sorceries', st.types.Sorcery], ['Artifacts', st.types.Artifact], ['Enchantments', st.types.Enchantment], ['Planeswalkers', st.types.Planeswalker]].filter(([, n]) => n > 0)
        if (st.types.Land > 0 || st.mdfc > 0) typeRows.push(['Lands/MDFC', st.mdfc > 0 ? `${st.types.Land}+${st.mdfc}` : `${st.types.Land}`])
        if (st.types.Other > 0) typeRows.push(['Other', st.types.Other])
        let PIPC = {W: '#efe9c8', U: '#4a90d9', B: '#6a4c93', R: '#e0625c', G: '#5cb85c'}
        let PIPS = ['W', 'U', 'B', 'R', 'G']
        return (
          <div style={s.statsPanel}>
            <div style={s.statsCol}>
              <div style={s.statsHeading}>Types</div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px 12px'}}>
                {typeRows.map(([label, n]) => (
                  <span key={label} style={s.statChip}><b style={{color: c.text}}>{n}</b> {label}</span>
                ))}
              </div>
            </div>
            <div style={s.statsCol}>
              <div style={s.statsHeading}>Mana curve <span style={{color: c.textDim, fontWeight: 400, textTransform: 'none', letterSpacing: 0}}>· avg {st.avgCmc.toFixed(2)}</span></div>
              <div style={{display: 'flex', alignItems: 'flex-end', gap: 4, height: 56}}>
                {st.curve.map((n, i) => (
                  <div key={i} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1}}>
                    <span style={{fontSize: 9, color: c.textDim, lineHeight: 1, height: 10}}>{n || ''}</span>
                    <div style={{width: '100%', height: Math.round((n / st.curveMax) * 38), minHeight: n ? 3 : 0, background: c.accent, borderRadius: 2, transition: 'height 0.2s ease'}} />
                    <span style={{fontSize: 9, color: c.textDim, lineHeight: 1}}>{i === 7 ? '7+' : i}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={s.statsCol}>
              <div style={s.statsHeading}>Color pips</div>
              {st.pipTotal === 0 ? (
                <div style={{color: c.textDim, fontSize: 12}}>No colored pips</div>
              ) : (
                <>
                  <div style={{display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: c.panel2}}>
                    {PIPS.filter(p => st.pips[p] > 0).map(p => (
                      <div key={p} style={{width: `${(st.pips[p] / st.pipTotal) * 100}%`, background: PIPC[p]}} title={`${p}: ${st.pips[p]}`} />
                    ))}
                  </div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 7}}>
                    {PIPS.filter(p => st.pips[p] > 0).map(p => (
                      <span key={p} style={{display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: c.textDim}}>
                        <span style={{width: 9, height: 9, borderRadius: '50%', background: PIPC[p]}} />
                        {Math.round((st.pips[p] / st.pipTotal) * 100)}% <span style={{opacity: 0.6}}>({st.pips[p]})</span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )
      })()}

      {/* Deck as solitaire "rails": each rail is a vertical stack of categories. Drag a category by
          its ⠿ handle to stack it under another (fewer, wider columns) or onto the "+ column" strip
          to pull it back out. Drag a card tile to re-file every copy into another category. */}
      {flat.length > 0 && (
        <>
          <div style={{...s.columns, display: 'flex', flexWrap: 'nowrap', gap: GAP, justifyContent: 'center', alignItems: 'flex-start'}}>
            <div style={{position: 'relative', flexShrink: 0, height: masonryH || undefined, width: railCols * (CARD_W + GAP) - GAP}}>
              {rails.map((rail, ri) => {
                let pos = colPos[ri] || {x: (ri % railCols) * (CARD_W + GAP), y: 0}
                return (
                  <div
                    key={rail[0]}
                    style={{position: 'absolute', left: pos.x, top: pos.y, width: CARD_W, height: pos.availH || undefined, display: 'flex', flexDirection: 'column', transition: 'left 0.2s ease, top 0.2s ease'}}>
                    <div ref={el => (railRefs.current[rail[0]] = el)} style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                    {rail.map(catKey => {
                      let ci = catIndexByKey(catKey)
                      if (ci < 0) return null
                      let cat = cats[ci]
                      return (
                        <Draggable key={catKey} id={'cat-' + catKey}>
                          {({attributes, listeners, setNodeRef: setDragRef, isDragging}) => (
                            <Droppable id={'col-' + ci}>
                              {({setNodeRef: setDropRef, isOver}) => (
                                <>
                                <div
                                  ref={el => { setDragRef(el); setDropRef(el) }}
                                  style={{...s.column, width: CARD_W, opacity: isDragging ? 0.4 : 1, borderRadius: 6, ...(isOver && !draggingCat && !isDragging ? s.columnOver : null)}}>
                                  <div style={{...s.colHeader, position: 'relative'}}>
                                    <span {...attributes} {...listeners} style={s.dragHandle} title="Drag to move or stack this category">⠿</span>
                                    <input
                                      value={cat.label}
                                      placeholder="Category"
                                      autoFocus={cat.key === focusColKey}
                                      onFocus={e => { setFocusColKey(cat.key); if (!cat.label) e.target.select() }}
                                      onBlur={() => setFocusColKey(k => (k === cat.key ? null : k))}
                                      onChange={e => renameCategory(cat.key, e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                                      style={s.colHeaderInput}
                                    />
                                    {columns[ci].length ? <span style={{...s.colCount, marginRight: 2}}>{columns[ci].length}</span> : null}
                                    <button
                                      style={{...s.catCog, opacity: catSettingsKey === cat.key ? 1 : 0.3}}
                                      title="Category settings"
                                      onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
                                      onMouseLeave={e => (e.currentTarget.style.opacity = catSettingsKey === cat.key ? 1 : 0.3)}
                                      onClick={() => setCatSettingsKey(k => (k === cat.key ? null : cat.key))}>
                                      ⚙
                                    </button>
                                    <button
                                      style={s.colDelete}
                                      title="Delete category"
                                      onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
                                      onMouseLeave={e => (e.currentTarget.style.opacity = 0.3)}
                                      onClick={() => deleteCategory(cat.key)}>
                                      ×
                                    </button>
                                    {catSettingsKey === cat.key && (
                                      <>
                                        <div style={{position: 'fixed', inset: 0, zIndex: 55}} onClick={() => setCatSettingsKey(null)} />
                                        <div style={s.catSettingsPop}>
                                          <div style={{...s.settingRow, cursor: 'pointer'}} onClick={() => setCatExclude(cat.key, !cat.excludePrice)}>
                                            <span style={{flexShrink: 0, width: 10, height: 10, marginTop: 2, borderRadius: '50%', boxSizing: 'border-box', transition: 'background 0.12s ease', ...(cat.excludePrice ? {background: 'transparent', border: `1px solid ${c.textDim}`} : {background: c.accent})}} />
                                            <span>
                                              Include in deck price
                                              <span style={{display: 'block', color: c.textDim, fontSize: 10, marginTop: 2}}>Off = left out of the total at the top — like a sideboard / maybeboard.</span>
                                            </span>
                                          </div>
                                          <div style={{...s.settingRow, cursor: 'pointer', marginTop: 12}} onClick={() => setCatExportExclude(cat.key, !cat.excludeExport)}>
                                            <span style={{flexShrink: 0, width: 10, height: 10, marginTop: 2, borderRadius: '50%', boxSizing: 'border-box', transition: 'background 0.12s ease', ...(cat.excludeExport ? {background: 'transparent', border: `1px solid ${c.textDim}`} : {background: c.accent})}} />
                                            <span>
                                              Include in export
                                              <span style={{display: 'block', color: c.textDim, fontSize: 10, marginTop: 2}}>Off = this category's cards are skipped when you press Export.</span>
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div style={{...s.colStack, minHeight: CARD_H}}>
                                    {groupedColumns[ci].length === 0
                                      ? <div style={{width: CARD_W, height: CARD_H, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.6)'}} />
                                      : groupedColumns[ci].map((group, i) => renderTile(group, i, groupedColumns[ci]))}
                                  </div>
                                </div>
                                {isOver && draggingCat && !isDragging && (
                                  <div style={{...s.phantomSlot, width: CARD_W, height: CARD_H}}>{draggedCat?.label || 'New spot'}</div>
                                )}
                                </>
                              )}
                            </Droppable>
                          )}
                        </Draggable>
                      )
                    })}
                    </div>
                    {pos.availH ? (
                      <Droppable id={'railtail-' + rail[0]}>
                        {({setNodeRef, isOver}) => (
                          <div ref={setNodeRef} style={{flex: 1, marginTop: 12, display: 'flex', flexDirection: 'column'}}>
                            {isOver && draggingCat && (
                              <div style={{...s.phantomSlot, width: CARD_W, height: CARD_H}}>{draggedCat?.label || 'New spot'}</div>
                            )}
                          </div>
                        )}
                      </Droppable>
                    ) : null}
                  </div>
                )
              })}
              {/* Between-column insertion carets: while dragging a category, each visual column's
                  left gap is a drop zone that pulls the category out into a NEW column right there
                  (reordering columns). The top rail of each column (pos.y===0) owns its left gap. */}
              {draggingCat && rails.map((rail, ri) => {
                let pos = colPos[ri] || {x: (ri % railCols) * (CARD_W + GAP), y: 0}
                if (pos.y) return null
                return (
                  <Droppable id={'gap-' + rail[0]} key={'gap-' + rail[0]}>
                    {({setNodeRef, isOver}) => (
                      <div ref={setNodeRef} style={{position: 'absolute', top: 0, left: pos.x - GAP / 2 - 10, width: 20, height: masonryH || CARD_H, zIndex: 6, display: 'flex', justifyContent: 'center', pointerEvents: 'none'}}>
                        <div style={{width: isOver ? 4 : 2, height: '100%', borderRadius: 2, background: isOver ? c.accent : 'rgba(77,163,255,0.28)', boxShadow: isOver ? '0 0 10px rgba(77,163,255,0.85)' : 'none', transition: 'width 0.1s ease, background 0.1s ease'}} />
                      </div>
                    )}
                  </Droppable>
                )
              })}
            </div>
            {draggingCat && (
              <Droppable id="newcol">
                {({setNodeRef, isOver}) => (
                  <div ref={setNodeRef} style={{...s.newColStrip, ...(isOver ? s.newColStripOver : null), height: masonryH || CARD_H}}>
                    + column
                  </div>
                )}
              </Droppable>
            )}
          </div>
          <DragOverlay dropAnimation={null}>
            {dragCard ? (
              <img src={dragCard.url} alt={dragCard.name} draggable={false} style={{width: CARD_W, height: CARD_H, borderRadius: 8, display: 'block', boxShadow: '0 10px 28px rgba(0,0,0,0.7)', cursor: 'grabbing'}} />
            ) : draggedCat ? (
              <div style={s.catDragChip}>⠿ {draggedCat.label || 'Category'}</div>
            ) : null}
          </DragOverlay>
        </>
      )}
      </DndContext>
    </div>

    {/* Deck switcher: slides in from the right; commander art + name (custom name if renamed). */}
    {showDeckList && (
      <>
        <div style={s.deckListBackdrop} onClick={() => setShowDeckList(false)} />
        <div className="thinScroll" style={s.deckListPanel}>
          <div style={s.deckListHeader}>
            <span>Your decks</span>
            <button style={s.artClose} onClick={() => setShowDeckList(false)} title="Close" onMouseOver={e => (e.currentTarget.style.color = c.text)} onMouseOut={e => (e.currentTarget.style.color = c.textDim)}>✕</button>
          </div>
          {currentUser && (
            <div style={s.deckListUser}>
              <span style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.textDim} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
                </svg>
                <span style={{fontSize: 13, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{currentUser.displayName || currentUser.username}</span>
              </span>
              <button style={s.linkBtn} onClick={handleLogout}>log out</button>
            </div>
          )}
          {decks.length === 0 ? (
            <div style={{color: c.textDim, fontSize: 12}}>No decks yet — import one to get started.</div>
          ) : (
            decks.map(d => {
              let art = deckArt(d)
              let cmName = d.commander?.name
              let sub = cmName && cmName !== d.name ? cmName : null
              return (
                <div
                  key={d.id}
                  style={{...s.deckListItem, ...(d.id === activeDeckId ? s.deckListItemActive : null)}}
                  onClick={() => { setActiveDeckId(d.id); setShowDeckList(false) }}
                  onMouseOver={e => { if (d.id !== activeDeckId) e.currentTarget.style.background = c.panel2 }}
                  onMouseOut={e => { if (d.id !== activeDeckId) e.currentTarget.style.background = 'transparent' }}>
                  {art ? <img src={art} alt="" style={s.deckListArt} /> : <div style={{...s.deckListArt, background: c.panel2}} />}
                  <div style={{minWidth: 0, flex: 1}}>
                    <div style={s.deckListName}>{d.name}</div>
                    {sub && <div style={s.deckListSub}>{sub}</div>}
                    <div style={s.deckListMeta}>
                      {deckCardCount(d)} cards
                      {deckPriceOf(d) > 0 && <span style={s.deckListPrice}> · ${deckPriceOf(d).toFixed(2)}</span>}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </>
    )}

    {artCard && (
      <>
      {/* Click anywhere outside the picker closes it */}
      <div style={s.artBackdrop} onClick={() => setArtCard(null)} />
      <div
        className="thinScroll"
        style={s.artPanel}
        onScroll={e => {
          let el = e.currentTarget
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) setShownCount(n => Math.min(n + 12, variants.length))
        }}>
        {/* Giant edge-to-edge image of the currently-applied printing (updates as you pick one below) */}
        <div style={s.artHeroWrap}>
          <img
            src={(artDetail && artDetail.image_uri) || artCard.card.url || artCard.card.scryfallUrl}
            alt={artCard.card.name}
            style={s.artHero}
            onError={e => { if (artCard.card.scryfallUrl && e.target.src !== artCard.card.scryfallUrl) e.target.src = artCard.card.scryfallUrl }}
          />
          <button style={s.artCloseFloat} onClick={() => setArtCard(null)} title="Close" onMouseOver={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.8)')} onMouseOut={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.55)')}>
            ✕
          </button>
        </div>
        <div style={s.artNameRow}>
          <span style={s.artNameBig}>{artCard.card.name}</span>
          {artDetail && artDetail.prices && artDetail.prices.usd && <span style={s.artPriceBig}>${artDetail.prices.usd}</span>}
        </div>
        {artDetail && (
          <div style={s.artDetail}>
            {(artDetail.type_line || artDetail.mana_cost) && (
              <div style={s.artDetailTypeRow}>
                <span style={s.artDetailType}>{artDetail.type_line}</span>
                {artDetail.mana_cost && <span style={s.artDetailMana}>{renderMana(artDetail.mana_cost)}</span>}
              </div>
            )}
            {artDetail.oracle_text && <div style={s.artDetailOracle}>{artDetail.oracle_text}</div>}
            {artDetail.flavor_text && <div style={s.artFlavor}>{artDetail.flavor_text}</div>}
            <div style={s.artDetailMeta}>
              {artDetail.rarity && <span style={s.artDetailRarity}>{artDetail.rarity}</span>}
              {artDetail.set_name && <span style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{artDetail.set_name}</span>}
              <span style={{marginLeft: 'auto', display: 'flex', gap: 10, flexShrink: 0}}>
                <span>TCG <b style={{color: artDetail.prices?.usd ? '#4ade80' : c.textDim}}>{artDetail.prices?.usd ? `$${artDetail.prices.usd}` : '—'}</b></span>
                <span>CM <b style={{color: artDetail.prices?.eur ? c.text : c.textDim}}>{artDetail.prices?.eur ? `€${artDetail.prices.eur}` : '—'}</b></span>
              </span>
            </div>
            {artDetail.legalities && (
              <div style={s.artLegalWrap}>
                <div style={s.artLegalLabel}>Legality</div>
                <div style={s.artLegalGrid}>
                  {LEGALITY_FORMATS.map(([key, label]) => {
                    let badge = legalityBadge(key, artDetail.legalities[key] || 'not_legal', artDetail.game_changer)
                    return (
                      <div key={key} style={s.artLegalCell}>
                        <span style={{...s.artLegalBadge, background: badge.bg}}>{badge.label}</span>
                        <span style={s.artLegalName}>{label}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div style={s.artPrintingsLabel}>Printings{variants.length ? ` (${variants.length})` : ''}</div>
        {loadingVariants && <div style={s.empty}>Loading printings...</div>}
        {!loadingVariants && variantError && (
          <div style={s.empty}>
            Couldn't load printings — Scryfall rate limit.{' '}
            <button style={s.linkBtn} onClick={() => setRetryTick(t => t + 1)}>
              retry
            </button>
          </div>
        )}
        {!loadingVariants && !variantError && variants.length === 0 && <div style={s.empty}>No printings found.</div>}
        <div style={s.artGrid}>
          {variants.slice(0, shownCount).map(v => {
            let isCurrent = v.id === artCard.card.scryfallId // the printing currently applied to this deck card
            return (
              <div
                key={v.id}
                style={{...s.artItem, position: 'relative', ...(isCurrent ? {borderColor: '#f0c14b'} : {})}}
                title={`${v.set_name} #${v.collector_number}${v.usd != null ? ` · $${v.usd.toFixed(2)}` : ''}${isCurrent ? ' · current' : ''}`}
                onClick={() => chooseVariant(v)}
                onMouseOver={e => (e.currentTarget.style.borderColor = c.accent)}
                onMouseOut={e => (e.currentTarget.style.borderColor = isCurrent ? '#f0c14b' : 'transparent')}>
                <img src={v.image_uri} alt={v.set_name} loading="lazy" style={{width: '100%', display: 'block', borderRadius: 3}} />
                <span style={s.artPrice}>{v.usd != null ? `$${v.usd.toFixed(2)}` : '—'}</span>
              </div>
            )
          })}
        </div>
        {!loadingVariants && shownCount < variants.length && (
          <button style={{...s.primaryBtn, width: '100%', marginTop: 10}} onClick={() => setShownCount(n => Math.min(n + 12, variants.length))}>
            Show more ({variants.length - shownCount})
          </button>
        )}
      </div>
      </>
    )}
    </>
  )
}

// Render a Scryfall mana cost string ("{2}{R}{R}") as small colored pips for the art-picker detail header.
const MANA_COLORS = {W: '#f5f0d8', U: '#a9d4ea', B: '#c7bfba', R: '#f0a394', G: '#9fcfa4', C: '#cbc5c0'}
function renderMana(cost) {
  if (!cost) return null
  let syms = cost.match(/\{[^}]+\}/g) || []
  return syms.map((sym, i) => {
    let inner = sym.slice(1, -1) // 'R', '2', 'W/U', etc.
    let bg = MANA_COLORS[inner] || '#3a3a3a'
    let known = !!MANA_COLORS[inner]
    return (
      <span key={i} style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: bg, color: known ? '#1a1a1a' : c.textDim, fontSize: 9.5, fontWeight: 700, marginLeft: 2}}>
        {inner.replace(/\//g, '')}
      </span>
    )
  })
}

let s = {
  panel: {background: c.panel, borderRadius: 3, padding: 8, boxSizing: 'border-box', minHeight: '100%'},
  topRow: {display: 'flex', gap: 12, marginBottom: 4, alignItems: 'flex-start'},
  portrait: {width: 80, height: 70, flexShrink: 0, objectFit: 'cover', borderRadius: 8, background: c.bg, boxShadow: '0 2px 10px rgba(0,0,0,0.5)'},
  statsBtn: {background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '5px 0', fontSize: 12, cursor: 'pointer', width: '100%', textAlign: 'center', transition: 'color 0.12s ease, border-color 0.12s ease'},
  statsPanel: {display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start', background: c.bg, border: `1px solid ${c.panel2}`, borderRadius: 6, padding: '12px 16px', marginBottom: 12},
  statsCol: {flex: '1 1 190px', minWidth: 160},
  statsHeading: {fontSize: 11, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 9, fontWeight: 600},
  statChip: {fontSize: 12, color: c.textDim, whiteSpace: 'nowrap'},
  aiWrap: {marginBottom: 12},
  aiToggle: {background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'color 0.12s ease, border-color 0.12s ease'},
  aiPanel: {marginTop: 8},
  aiSelect: {background: c.bg, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '6px 8px', fontSize: 12, cursor: 'pointer'},
  aiGrid: {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(94px, 1fr))', gap: 10},
  sizeBtn: {width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0},
  aiCard: {cursor: 'pointer', border: '2px solid transparent', borderRadius: 8, padding: 3, transition: 'border-color 0.12s ease'},
  aiCardImg: {width: '100%', borderRadius: 6, display: 'block'},
  aiCardPrice: {position: 'absolute', bottom: 4, left: 5, fontSize: 11, fontWeight: 600, color: '#b6f0c6', opacity: 0.8, textShadow: '0 1px 2px rgba(0,0,0,0.85)'},
  aiCardStub: {width: '100%', aspectRatio: '488 / 680', borderRadius: 6, background: c.panel2, color: c.textDim, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 4, boxSizing: 'border-box'},
  aiCardWhy: {fontSize: 10, color: c.textDim, marginTop: 4, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'},
  topRight: {flex: 1, minWidth: 0},
  header: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12},
  title: {fontSize: 15, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: c.text},
  titleWrap: {display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0},
  titleName: {display: 'inline-block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15, fontWeight: 600, color: c.text, cursor: 'pointer', borderBottom: '1px solid transparent', transition: 'border-color 0.12s ease'},
  titleEdit: {fontSize: 15, fontWeight: 600, background: c.bg, color: c.text, border: `1px solid ${c.accent}`, borderRadius: 3, padding: '2px 6px', outline: 'none', minWidth: 0, width: 200},
  deckSwitchBtn: {display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 170, background: 'transparent', color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 4, padding: '5px 10px', fontSize: 13, cursor: 'pointer'},
  deckListBackdrop: {position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(0,0,0,0.4)'},
  deckListPanel: {position: 'fixed', right: 0, top: 0, bottom: 0, width: 300, maxWidth: '85vw', background: c.panel, zIndex: 111, padding: '16px 14px', boxSizing: 'border-box', overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.5)'},
  deckListHeader: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, fontSize: 14, color: c.text},
  deckListUser: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '0 4px 12px', marginBottom: 10, borderBottom: `1px solid ${c.panel2}`},
  deckListPrice: {color: '#a7e0b5'},
  deckListItem: {display: 'flex', gap: 10, alignItems: 'center', padding: 8, borderRadius: 6, cursor: 'pointer', marginBottom: 6, border: '1px solid transparent', transition: 'background 0.12s ease'},
  deckListItemActive: {border: `1px solid ${c.accent}`, background: 'rgba(77,163,255,0.08)'},
  deckListArt: {width: 52, height: 38, objectFit: 'cover', borderRadius: 4, flexShrink: 0},
  deckListName: {fontSize: 13, color: c.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
  deckListSub: {fontSize: 11, color: c.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1},
  deckListMeta: {fontSize: 11, color: c.textDim, marginTop: 2},
  deckCount: {color: c.textDim, fontSize: 13},
  deckTotal: {color: '#4ade80', fontSize: 13, fontWeight: 500},
  optTrack: {width: 54, height: 4, background: c.panel2, borderRadius: 2, overflow: 'hidden', display: 'inline-block'},
  optFill: {display: 'block', height: '100%', background: '#4ade80', transition: 'width 0.2s ease'},
  userWrap: {display: 'flex', alignItems: 'center', gap: 8},
  userName: {color: c.accent, fontSize: 13},
  authBox: {background: c.bg, borderRadius: 3, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8},
  controls: {display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12},
  emptyDeck: {background: c.bg, borderRadius: 3, marginTop: 4, display: 'flex', flexDirection: 'column', minHeight: 440, gap: 10},
  emptyTopRow: {flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '24px 16px', position: 'relative'},
  emptyImportBtn: {flex: 1, minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: c.panel, border: `2px dashed ${c.panel2}`, borderRadius: 8, color: c.text, cursor: 'pointer', transition: 'border-color 0.12s ease, background 0.12s ease'},
  emptyName: {color: c.text, fontSize: 16, fontWeight: 500, marginBottom: 6},
  addSearch: {position: 'relative', marginBottom: 12, zIndex: 20},
  addInput: {width: '100%', boxSizing: 'border-box', background: c.bg, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '8px 10px', fontSize: 13, outline: 'none'},
  addResults: {position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: c.panel, border: `1px solid ${c.panel2}`, borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 320, overflowY: 'auto', zIndex: 30},
  addResult: {display: 'flex', alignItems: 'center', gap: 8, padding: 6, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)'},
  addThumb: {width: 32, height: 44, flexShrink: 0, borderRadius: 3, objectFit: 'cover', background: c.bg},
  addResultText: {overflow: 'hidden', minWidth: 0},
  addResultName: {fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
  addResultSet: {fontSize: 11, color: c.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
  addResultEmpty: {padding: 10, fontSize: 13, color: c.textDim},
  pendingBox: {background: c.bg, borderRadius: 3, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8},
  select: {flex: 1, background: c.bg, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '8px 10px', fontSize: 13},
  nameEdit: {flex: 1, minWidth: 0, background: c.bg, color: c.text, border: `1px solid ${c.accent}`, borderRadius: 3, padding: '8px 10px', fontSize: 13, outline: 'none'},
  input: {background: c.bg, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '8px 10px', fontSize: 14},
  primaryBtn: {background: c.accent, color: '#fff', border: 'none', borderRadius: 3, padding: '8px 14px', fontSize: 13, cursor: 'pointer'},
  subtleBtn: {background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '8px 12px', fontSize: 13, cursor: 'pointer'},
  iconBtn: {background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: '6px 12px', fontSize: 16, lineHeight: 1, cursor: 'pointer'},
  linkBtn: {background: 'transparent', color: c.textDim, border: 'none', fontSize: 12, cursor: 'pointer', textDecoration: 'underline'},
  importBox: {background: c.bg, borderRadius: 3, padding: 10, marginBottom: 12},
  textarea: {width: '100%', boxSizing: 'border-box', minHeight: 120, background: c.panel, color: c.text, border: `1px solid ${c.panel2}`, borderRadius: 3, padding: 10, fontSize: 13, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit'},
  importFooter: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, gap: 8},
  progressTrack: {height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden', marginTop: 10},
  progressFill: {height: '100%', background: c.accent, borderRadius: 3, transition: 'width 0.25s ease'},
  stack: {display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4},
  empty: {color: c.textDim, fontSize: 13, textAlign: 'center', padding: '30px 10px'},
  columns: {display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', alignItems: 'flex-start', paddingTop: 4},
  column: {flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center'},
  columnOver: {outline: `2px dashed ${c.accent}`, outlineOffset: 3, borderRadius: 3, background: 'rgba(77,163,255,0.08)'},
  colHeader: {display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6, width: '100%', boxSizing: 'border-box'},
  colHeaderInput: {flex: 1, minWidth: 0, width: '100%', background: 'transparent', border: 'none', outline: 'none', color: c.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', padding: 0, margin: 0, fontFamily: 'inherit'},
  colCount: {flexShrink: 0, marginLeft: 'auto', fontSize: 10, color: c.textDim, letterSpacing: 0.5},
  colDelete: {flexShrink: 0, background: 'transparent', border: 'none', color: c.text, cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 2px', opacity: 0.3, transition: 'opacity 0.12s ease'},
  dragHandle: {flexShrink: 0, cursor: 'grab', color: c.textDim, fontSize: 12, lineHeight: 1, userSelect: 'none', touchAction: 'none', padding: '0 1px'},
  newColStrip: {flexShrink: 0, width: 40, minWidth: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', writingMode: 'vertical-rl', border: `2px dashed ${c.panel2}`, borderRadius: 6, color: c.textDim, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1},
  newColStripOver: {borderColor: c.accent, color: c.accent, background: 'rgba(77,163,255,0.08)'},
  catDragChip: {padding: '6px 10px', background: c.panel, border: `1px solid ${c.accent}`, borderRadius: 4, color: c.text, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, boxShadow: '0 10px 28px rgba(0,0,0,0.7)', cursor: 'grabbing', whiteSpace: 'nowrap'},
  phantomSlot: {boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', border: `2px dashed ${c.accent}`, borderRadius: 8, background: 'rgba(77,163,255,0.10)', color: c.accent, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 8px'},
  gearBtn: {background: 'transparent', border: 'none', color: c.textDim, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 2},
  headerIconBtn: {display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 32, boxSizing: 'border-box', background: 'transparent', color: c.textDim, border: `1px solid ${c.panel2}`, borderRadius: 3, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0},
  catCog: {flexShrink: 0, background: 'transparent', border: 'none', color: c.text, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '0 1px', transition: 'opacity 0.12s ease'},
  catSettingsPop: {position: 'absolute', top: '100%', right: 0, marginTop: 4, width: 178, background: c.panel, border: `1px solid ${c.panel2}`, borderRadius: 6, padding: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.55)', zIndex: 60, textTransform: 'none', letterSpacing: 0, textAlign: 'left', cursor: 'default'},
  settingsPop: {position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 232, background: c.panel, border: `1px solid ${c.panel2}`, borderRadius: 6, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 50},
  settingRow: {display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: c.text, cursor: 'pointer', lineHeight: 1.4},
  colStack: {display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'},
  cardBottom: {position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5},
  priceTag: {background: 'rgba(0,0,0,0.8)', borderRadius: 3, padding: '2px 8px', fontSize: 10, lineHeight: 1.3, color: c.textDim, whiteSpace: 'nowrap'},
  cardControls: {display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.75)', borderRadius: 3, padding: '4px 8px'},
  stepBtn: {width: 26, height: 26, borderRadius: 3, border: 'none', background: c.panel2, color: '#fff', fontSize: 16, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'},
  count: {color: '#fff', fontSize: 13, minWidth: 16, textAlign: 'center'},
  countBadge: {position: 'absolute', top: 5, right: 5, minWidth: 20, height: 20, padding: '0 6px', boxSizing: 'border-box', borderRadius: 3, background: 'rgba(0,0,0,0.82)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.6)'},
  artPanel: {position: 'fixed', left: 0, top: 0, bottom: 0, width: 340, background: c.panel, borderRadius: 3, padding: '54px 14px 14px', boxSizing: 'border-box', overflowY: 'auto', zIndex: 100, boxShadow: '4px 0 24px rgba(0,0,0,0.5)'},
  artDetail: {marginBottom: 12, borderBottom: `1px solid ${c.panel2}`, paddingBottom: 12},
  artDetailTypeRow: {display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6},
  artDetailType: {fontSize: 12.5, color: c.text, fontWeight: 500, flex: 1, minWidth: 0},
  artDetailMana: {display: 'inline-flex', alignItems: 'center', flexShrink: 0},
  artDetailOracle: {fontSize: 12, color: c.textDim, lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 8},
  artDetailMeta: {display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: c.textDim},
  // Flavor text: italic, muted metallic gold (below the oracle text, before the legality table).
  artFlavor: {fontSize: 11.5, color: '#bfa878', fontStyle: 'italic', lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: '-2px 0 8px'},
  // Legality mini-table: format on the left, color-coded status on the right.
  artLegalWrap: {marginTop: 10, borderTop: `1px solid ${c.panel2}`, paddingTop: 10},
  artLegalLabel: {fontSize: 10, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 8},
  artLegalGrid: {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px'},
  artLegalCell: {display: 'flex', alignItems: 'center', gap: 7, minWidth: 0},
  artLegalBadge: {fontSize: 8, fontWeight: 700, letterSpacing: 0.2, color: '#fff', padding: '3px 4px', borderRadius: 3, textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0, width: 62, textAlign: 'center', boxSizing: 'border-box'},
  artLegalName: {fontSize: 11, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
  artDetailRarity: {textTransform: 'capitalize', color: c.text, flexShrink: 0},
  artPrintingsLabel: {fontSize: 11, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, margin: '2px 0 8px'},
  artPrice: {position: 'absolute', left: 5, bottom: 5, background: 'rgba(0,0,0,0.82)', color: '#4ade80', fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 3, pointerEvents: 'none'},
  artBackdrop: {position: 'fixed', inset: 0, zIndex: 99, background: 'transparent'},
  artClose: {background: 'transparent', border: 'none', color: c.textDim, fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: 2, transition: 'color 0.15s ease'},
  artHeader: {display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6},
  artTitle: {fontSize: 14, fontWeight: 500, color: c.text, lineHeight: 1.3},
  // Card-detail hero: a giant edge-to-edge image at the top of the picker (breaks out of the panel's
  // 54px/14px padding), then the promoted name + price, then the existing details + printings below.
  artHeroWrap: {position: 'relative', margin: '-54px -14px 12px', lineHeight: 0},
  artHero: {width: '100%', display: 'block'},
  artCloseFloat: {position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', fontSize: 15, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s ease'},
  artNameRow: {display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 10},
  artNameBig: {fontSize: 17, fontWeight: 600, color: c.text, lineHeight: 1.25, minWidth: 0},
  artPriceBig: {fontSize: 16, fontWeight: 700, color: '#4ade80', flexShrink: 0},
  artGrid: {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8},
  artItem: {cursor: 'pointer', borderRadius: 3, overflow: 'hidden', border: '2px solid transparent'},
}

export default DeckPanel
